import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  IconX,
  IconEdit,
  IconId,
  IconMail,
  IconUser,
  IconPhone,
  IconBracketsAngle,
  IconBriefcase,
  IconFileText,
  IconCalendar,
  IconRotate,
  IconPlaneDeparture,
  IconAward,
  IconLock,
  IconDeviceLaptop,
  IconFingerprint,
  IconShield,
  IconCalendarEvent,
  IconCreditCard,
  IconCategory,
  IconUsers,
  IconCertificate,
  IconClock,
  IconListDetails,
  IconSearch,
  IconChevronRight,
  IconMapPin,
  IconArrowLeft,
  IconPlus,
  IconUpload,
  IconClipboardCheck,
  IconClipboardX,
  IconAlertCircle,
  IconClockPause,
  IconTrash,
  IconDownload,
  IconCheck,
  IconEye,
  IconCircleX,
  IconDots,
  IconDeviceFloppy,
  IconTrendingUp,
  IconTrendingDown,
  IconAlertTriangle,
  IconGift,
} from "@tabler/icons-react";
import {
  fetchHrEmployees,
  fetchHrLeaveRequests,
  createHrLeaveRequest,
  deleteHrLeaveRequest,
  fetchHrLeaveTypes,
  createHrLeaveType,
  updateHrLeaveStatus,
  fetchHrDepartments,
  fetchHrDesignations,
  fetchHrRoles,
  updateHrEmployee,
  fetchHrEmployeeById,
  verifyHrEmployeeDocument,
  deleteHrEmployeeDocument,
  updateHrLeaveType,
  updateHrLeaveRequest,
  fetchHrAttendanceCalendar,
} from "../../../apis/hrApi";
import toast from "react-hot-toast";

const TABS = [
  { label: "Personal Info", icon: <IconUser size={16} /> },
  { label: "Work", icon: <IconBriefcase size={16} /> },
  { label: "Documents", icon: <IconFileText size={16} /> },
  { label: "Attendance", icon: <IconClock size={16} /> },
  { label: "Regularization", icon: <IconRotate size={16} /> },
  { label: "Leaves", icon: <IconPlaneDeparture size={16} /> },
  { label: "Action", icon: <IconAward size={16} /> },
  { label: "Permissions", icon: <IconLock size={16} /> },
  { label: "Device Whitelist", icon: <IconDeviceLaptop size={16} /> },
];

export default function EmployeeDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [employees, setEmployees] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("Personal Info");

  // ── Leave tab state ──────────────────────────────────────
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<any[]>([]);
  const [isLeavesLoading, setIsLeavesLoading] = useState(false);
  const [showAddLeaveModal, setShowAddLeaveModal] = useState(false);
  const [isLeaveSubmitting, setIsLeaveSubmitting] = useState(false);
  // Add leave form fields
  const [leaveFormType, setLeaveFormType] = useState<"FULL_DAY" | "MULTIPLE" | "FIRST_HALF" | "SECOND_HALF">("FULL_DAY");
  const [leaveFormDate, setLeaveFormDate] = useState("");
  const [leaveFormEndDate, setLeaveFormEndDate] = useState("");
  const [leaveFormLeaveTypeId, setLeaveFormLeaveTypeId] = useState("");
  const [leaveFormStatus, setLeaveFormStatus] = useState("PENDING");
  const [leaveFormReason, setLeaveFormReason] = useState("");
  const [leaveFormDocument, setLeaveFormDocument] = useState<File | null>(null);

  // New Leave Type modal state
  const [showNewLeaveTypeModal, setShowNewLeaveTypeModal] = useState(false);
  const [isCreatingLeaveType, setIsCreatingLeaveType] = useState(false);
  const [newLeaveTypeName, setNewLeaveTypeName] = useState("");
  const [newLeaveTypeCount, setNewLeaveTypeCount] = useState("1");
  const [newLeaveTypeMonthlyLimit, setNewLeaveTypeMonthlyLimit] = useState("1");
  const [newLeaveTypeColor, setNewLeaveTypeColor] = useState("#FF0000");
  const [newLeaveTypePaid, setNewLeaveTypePaid] = useState(false);
  
  // Tabs and extra fields state
  const [newLeaveTypeTab, setNewLeaveTypeTab] = useState<"General" | "Entitlement" | "Applicability">("General");
  const [newLeaveTypeEffectiveAfter, setNewLeaveTypeEffectiveAfter] = useState("0");
  const [newLeaveTypeEffectiveUnit, setNewLeaveTypeEffectiveUnit] = useState<"DAYS" | "MONTHS" | "YEARS">("MONTHS");
  const [newLeaveTypeUnused, setNewLeaveTypeUnused] = useState<"CARRY_FORWARD" | "ENCASHMENT" | "EXPIRE">("CARRY_FORWARD");
  const [newLeaveTypeInNotice, setNewLeaveTypeInNotice] = useState(false);
  const [newLeaveTypeInProbation, setNewLeaveTypeInProbation] = useState(false);
  const [newLeaveTypePriorPermission, setNewLeaveTypePriorPermission] = useState(false);
  const [newLeaveTypeDepartments, setNewLeaveTypeDepartments] = useState<string[]>([]);
  const [newLeaveTypeDesignations, setNewLeaveTypeDesignations] = useState<string[]>([]);
  const [newLeaveTypeRoles, setNewLeaveTypeRoles] = useState<string[]>([]);
  const [newLeaveTypeGender, setNewLeaveTypeGender] = useState<"ALL" | "MALE" | "FEMALE">("ALL");
  const [newLeaveTypeMaritalStatus, setNewLeaveTypeMaritalStatus] = useState<"ALL" | "SINGLE" | "MARRIED">("ALL");

  // ── Attendance tab state ──────────────────────────────────
  const [attendanceData, setAttendanceData] = useState<any[]>([]);
  const [attendanceMonth, setAttendanceMonth] = useState<number>(new Date().getMonth() + 1);
  const [attendanceYear, setAttendanceYear] = useState<number>(new Date().getFullYear());
  const [isAttendanceLoading, setIsAttendanceLoading] = useState(false);

  // ── Bank Details Edit States ──────────────────────────────
  const [showEditBankModal, setShowEditBankModal] = useState(false);
  const [bankAccountHolderName, setBankAccountHolderName] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankAccountType, setBankAccountType] = useState("Savings Account");
  const [bankIfscCode, setBankIfscCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankBranchName, setBankBranchName] = useState("");
  const [bankBranchAddress, setBankBranchAddress] = useState("");
  const [isBankSubmitting, setIsBankSubmitting] = useState(false);

  // ── Lent/Return Requests States ───────────────────────────
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [showSendRequestModal, setShowSendRequestModal] = useState(false);
  const [assetRequests, setAssetRequests] = useState<any[]>([]);
  const [reqAsset, setReqAsset] = useState("");
  const [reqReturnDate, setReqReturnDate] = useState("");
  const [reqType, setReqType] = useState<"LENT" | "RETURN">("LENT");
  const [reqRemark, setReqRemark] = useState("");
  const [isReqSubmitting, setIsReqSubmitting] = useState(false);

  // ── Action tab state ──────────────────────────────────────
  const [actionSubTab, setActionSubTab] = useState<"Appreciations" | "Appraisal" | "Relegation" | "Fines & Warning" | "Rewards" | "Resign & Terminate">("Appreciations");
  const [appreciations, setAppreciations] = useState<any[]>([]);
  const [appraisals, setAppraisals] = useState<any[]>([]);
  const [relegations, setRelegations] = useState<any[]>([]);
  const [finesWarnings, setFinesWarnings] = useState<any[]>([]);
  const [rewards, setRewards] = useState<any[]>([]);
  const [resignTerminateInfo, setResignTerminateInfo] = useState<any | null>(null);

  // Appreciation modal form state
  const [showAddAppreciationModal, setShowAddAppreciationModal] = useState(false);
  const [appreciationAwardType, setAppreciationAwardType] = useState("");
  const [appreciationRecognizedBy, setAppreciationRecognizedBy] = useState("");
  const [appreciationGivenOn, setAppreciationGivenOn] = useState("");
  const [appreciationVisibility, setAppreciationVisibility] = useState("Public");
  const [appreciationSummary, setAppreciationSummary] = useState("");
  const [appreciationAwardTypesList, setAppreciationAwardTypesList] = useState<string[]>([
    "Employee of the Month",
    "Star Performer",
    "Best Team Player",
    "Customer Obsession Award"
  ]);
  const [showNewAwardTypeModal, setShowNewAwardTypeModal] = useState(false);
  const [newAwardTypeNameInput, setNewAwardTypeNameInput] = useState("");

  // Appraisal modal form state
  const [showAddAppraisalModal, setShowAddAppraisalModal] = useState(false);
  const [appraisalNewDesignation, setAppraisalNewDesignation] = useState("");
  const [appraisalEffectiveDate, setAppraisalEffectiveDate] = useState(new Date().toISOString().split("T")[0]);
  const [appraisalOldCtc, setAppraisalOldCtc] = useState("");
  const [appraisalNewCtc, setAppraisalNewCtc] = useState("");
  const [appraisalRemarks, setAppraisalRemarks] = useState("");

  // Relegation modal form state
  const [showAddRelegationModal, setShowAddRelegationModal] = useState(false);
  const [relegationNewDesignation, setRelegationNewDesignation] = useState("");
  const [relegationEffectiveDate, setRelegationEffectiveDate] = useState(new Date().toISOString().split("T")[0]);
  const [relegationOldCtc, setRelegationOldCtc] = useState("");
  const [relegationNewCtc, setRelegationNewCtc] = useState("");
  const [relegationRemarks, setRelegationRemarks] = useState("");

  // Fines/Warning modal form state
  const [showAddFineWarningModal, setShowAddFineWarningModal] = useState(false);
  const [fineType, setFineType] = useState<"FINE" | "WARNING">("FINE");
  const [fineAmount, setFineAmount] = useState("");
  const [fineDate, setFineDate] = useState("");
  const [fineReason, setFineReason] = useState("");

  // Reward modal form state
  const [showAddRewardModal, setShowAddRewardModal] = useState(false);
  const [rewardGivenOn, setRewardGivenOn] = useState(new Date().toISOString().split("T")[0]);
  const [rewardIsMonetary, setRewardIsMonetary] = useState(false);
  const [rewardAmount, setRewardAmount] = useState("");
  const [rewardDescription, setRewardDescription] = useState("");

  // Resign/Terminate modal form state
  const [showResignTerminateModal, setShowResignTerminateModal] = useState(false);
  const [resignTerminateActionType, setResignTerminateActionType] = useState<"RESIGN" | "TERMINATE">("RESIGN");
  const [resignTerminateDate, setResignTerminateDate] = useState("");
  const [resignTerminateReason, setResignTerminateReason] = useState("");
  const [resignTerminateFinalDay, setResignTerminateFinalDay] = useState("");

  const [departmentsList, setDepartmentsList] = useState<any[]>([]);
  const [designationsList, setDesignationsList] = useState<any[]>([]);
  const [rolesList, setRolesList] = useState<any[]>([]);

  // Dropdown open states for multi-selects in applicability tab
  const [isDepDropdownOpen, setIsDepDropdownOpen] = useState(false);
  const [isDesDropdownOpen, setIsDesDropdownOpen] = useState(false);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);

  // ── Employee Documents state ──────────────────────────────
  const [employeeDocuments, setEmployeeDocuments] = useState<any[]>([]);
  const [isDocsLoading, setIsDocsLoading] = useState(false);
  const [showUploadDocModal, setShowUploadDocModal] = useState(false);
  const [docModalMode, setDocModalMode] = useState<"ADD" | "EDIT">("ADD");
  const [selectedDocForEdit, setSelectedDocForEdit] = useState<any | null>(null);
  const [uploadDocType, setUploadDocType] = useState("");
  const [uploadDocFile, setUploadDocFile] = useState<File | null>(null);
  const [isDocUploading, setIsDocUploading] = useState(false);

  // ── Leave Types Inline Edit state ──────────────────────────
  const [editingLeaveTypeId, setEditingLeaveTypeId] = useState<string | null>(null);
  const [editAllocated, setEditAllocated] = useState<string>("");
  const [editMonthlyLimit, setEditMonthlyLimit] = useState<string>("");
  const [isLeaveTypeSaving, setIsLeaveTypeSaving] = useState(false);

  // ── Leave History Actions Popover state ──────────────────────
  const [activePopoverLeaveId, setActivePopoverLeaveId] = useState<string | null>(null);

  // ── Leave Modals state ──────────────────────────────────────
  const [showViewLeaveModal, setShowViewLeaveModal] = useState(false);
  const [selectedLeaveForView, setSelectedLeaveForView] = useState<any | null>(null);

  const [showEditLeaveModal, setShowEditLeaveModal] = useState(false);
  const [selectedLeaveForEdit, setSelectedLeaveForEdit] = useState<any | null>(null);
  const [isEditLeaveSubmitting, setIsEditLeaveSubmitting] = useState(false);

  const [showDeleteLeaveModal, setShowDeleteLeaveModal] = useState(false);
  const [selectedLeaveForDelete, setSelectedLeaveForDelete] = useState<any | null>(null);
  const [isDeleteLeaveSubmitting, setIsDeleteLeaveSubmitting] = useState(false);

  const activeEmployee = useMemo(() => {
    return employees.find((emp) => emp.id === id);
  }, [employees, id]);

  const emp: any = activeEmployee || {};
  const raw: any = emp.raw || {};

  const [activeEmployeeData, setActiveEmployeeData] = useState<any | null>(null);
  const [isEmployeeDetailsLoading, setIsEmployeeDetailsLoading] = useState(false);

  const loadEmployeeDetails = useCallback(async () => {
    if (!id) return;
    setIsEmployeeDetailsLoading(true);
    try {
      const res = await fetchHrEmployeeById(id);
      if (res.success && res.data) {
        setActiveEmployeeData(res.data);
      }
    } catch (err) {
      console.error("Failed to load employee details:", err);
    } finally {
      setIsEmployeeDetailsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEmployeeDetails();
  }, [loadEmployeeDetails]);

  useEffect(() => {
    if (!id) return;
    
    let list: any[] = [];
    if (activeEmployeeData && activeEmployeeData.docs && Array.isArray(activeEmployeeData.docs)) {
      list = activeEmployeeData.docs;
    } else if (raw.documents && Array.isArray(raw.documents)) {
      list = raw.documents;
    }

    if (list.length > 0) {
      const mapped = list.map((doc: any, index: number) => ({
        id: doc.id || doc._id || `doc-${index}`,
        name: doc.name || doc.documentName || "Document",
        type: doc.type || doc.documentType || "Other",
        url: doc.url || doc.attachmentUrl || "",
        color: doc.type === "Aadhar Card" ? "text-red-500" : doc.type === "PAN Card" ? "text-blue-500" : doc.type === "Work Contract" ? "text-emerald-500" : "text-slate-500",
        raw: doc
      }));
      setEmployeeDocuments(mapped);
    } else {
      setEmployeeDocuments([]);
    }
  }, [id, raw, activeEmployeeData]);

  const loadEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchHrEmployees();
      if (response.success && response.data?.employees) {
        const mapped = response.data.employees.map((emp: any) => ({
          id: emp.id || emp._id,
          empId: emp.employeeId || emp.empId || "-",
          name: emp.firstName
            ? `${emp.firstName} ${emp.lastName || ""}`
            : emp.name || "Unknown",
          email: emp.workEmail || emp.personalEmail || emp.email || "-",
          phone: emp.phoneNumber || emp.phone || "-",
          designation: emp.designationName || emp.designation || "-",
          joiningDate: emp.joiningDate
            ? new Date(emp.joiningDate).toLocaleDateString()
            : "-",
          status: emp.status || "ACTIVE",
          raw: emp,
        }));
        setEmployees(mapped);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load employees");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // Load leave data when Leaves tab is active and employee selected
  const loadLeaveData = React.useCallback(async () => {
    if (!id) return;
    setIsLeavesLoading(true);
    try {
      const filter = JSON.stringify([{ field: "employeeId", value: id! }]);
      const [reqRes, typeRes] = await Promise.all([
        fetchHrLeaveRequests(1, 100, filter),
        fetchHrLeaveTypes(1, 100),
      ]);
      if (reqRes.success && reqRes.data) {
        let requests: any[] = [];
        if (Array.isArray(reqRes.data)) requests = reqRes.data;
        else if (reqRes.data.leaveRequests) requests = reqRes.data.leaveRequests;
        else if (reqRes.data.leaveReports) requests = reqRes.data.leaveReports;
        else if (reqRes.data.leaves) requests = reqRes.data.leaves;
        else {
          const found = Object.values(reqRes.data).find(Array.isArray);
          if (found) requests = found as any[];
        }
        // Already filtered by employeeId on server
        setLeaveRequests(requests);
      }
      if (typeRes.success && typeRes.data) {
        let types: any[] = [];
        if (Array.isArray(typeRes.data)) types = typeRes.data;
        else if (typeRes.data.leaveTypes) types = typeRes.data.leaveTypes;
        else {
          const found = Object.values(typeRes.data).find(Array.isArray);
          if (found) types = found as any[];
        }
        setLeaveTypes(types);
      }
    } catch (err: any) {
      console.error("Error loading leave data:", err);
    } finally {
      setIsLeavesLoading(false);
    }
  }, [id]);

  const loadAttendanceData = React.useCallback(async () => {
    if (!id) return;
    setIsAttendanceLoading(true);
    try {
      const res = await fetchHrAttendanceCalendar(attendanceMonth, attendanceYear, 1, 100, "", id);
      if (res.success && res.data) {
        const d = res.data;
        let rawList: any[] = [];
        if (Array.isArray(d)) {
          rawList = d;
        } else if (d && typeof d === "object") {
          const possible = d.employeeCalendars || d.list || d.calendar || d.attendances || d.employees || d.records || d.data;
          if (Array.isArray(possible)) {
            rawList = possible;
          } else {
            const found = Object.values(d).find((v) => Array.isArray(v));
            if (found) rawList = found as any[];
          }
        }
        
        // Find this employee's calendar log
        const empItem = rawList.find((item: any) => {
          const empInfo = item.employee || item;
          const empId = empInfo.id || empInfo._id || item.employeeId;
          return String(empId) === String(id);
        });

        if (empItem) {
          const nested = empItem.calendar || empItem.attendances || empItem.records || empItem.attendance || [];
          setAttendanceData(nested);
        } else {
          setAttendanceData([]);
        }
      }
    } catch (err) {
      console.error("Failed to load attendance logs:", err);
    } finally {
      setIsAttendanceLoading(false);
    }
  }, [id, attendanceMonth, attendanceYear]);

  useEffect(() => {
    if (activeTab === "Attendance" && id) loadAttendanceData();
  }, [activeTab, id, loadAttendanceData]);

  // ── Work Tab Helper Handlers ──────────────────────────────
  const openEditBankModal = () => {
    setBankAccountHolderName(raw.accountHolderName || "");
    setBankAccountNumber(raw.accountNumber || "");
    setBankAccountType(raw.accountType || "Savings Account");
    setBankIfscCode(raw.ifscCode || "");
    setBankName(raw.bankName || "");
    setBankBranchName(raw.branchName || "");
    setBankBranchAddress(raw.branchAddress || "");
    setShowEditBankModal(true);
  };

  const handleEditBankSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setIsBankSubmitting(true);
    const loader = toast.loading("Updating bank details...");
    try {
      const updatedRaw = {
        ...raw,
        accountHolderName: bankAccountHolderName,
        accountNumber: bankAccountNumber,
        accountType: bankAccountType,
        ifscCode: bankIfscCode,
        bankName,
        branchName: bankBranchName,
        branchAddress: bankBranchAddress,
      };

      const res = await updateHrEmployee(id, { raw: updatedRaw });
      if (res.success) {
        toast.success("Bank details updated successfully!", { id: loader });
        setShowEditBankModal(false);
        loadEmployeeDetails();
        const empListRes = await fetchHrEmployees(1, 100);
        if (empListRes.success && empListRes.data?.employees) {
          setEmployees(empListRes.data.employees);
        }
      } else {
        toast.error(res.message || "Failed to update bank details", { id: loader });
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to update bank details", { id: loader });
    } finally {
      setIsBankSubmitting(false);
    }
  };

  const toggleBankVerification = async () => {
    if (!id) return;
    const loader = toast.loading("Updating verification status...");
    try {
      const updatedRaw = {
        ...raw,
        isBankVerified: !raw.isBankVerified,
      };
      const res = await updateHrEmployee(id, { raw: updatedRaw });
      if (res.success) {
        toast.success("Verification status updated!", { id: loader });
        loadEmployeeDetails();
        const empListRes = await fetchHrEmployees(1, 100);
        if (empListRes.success && empListRes.data?.employees) {
          setEmployees(empListRes.data.employees);
        }
      } else {
        toast.error(res.message || "Failed to update verification status", { id: loader });
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to update verification status", { id: loader });
    }
  };

  const handleSendRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reqAsset) {
      toast.error("Please enter an asset name");
      return;
    }
    setIsReqSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 800));
    const newRequest = {
      asset: reqAsset,
      returnDate: reqReturnDate,
      type: reqType,
      remark: reqRemark,
      status: "PENDING",
      approvedBy: "-",
    };
    setAssetRequests((prev) => [newRequest, ...prev]);
    toast.success("Request sent successfully!");
    setIsReqSubmitting(false);
    setShowSendRequestModal(false);
    setReqAsset("");
    setReqReturnDate("");
    setReqType("LENT");
    setReqRemark("");
  };

  // ── Action Tab Handlers ────────────────────────────────────
  const handleAddAppreciationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newAppr = {
      id: "mock-appr-" + Date.now(),
      name: emp.name || "Employee",
      award: appreciationAwardType || "Star Performer",
      recognizedBy: appreciationRecognizedBy || "Sanju",
      givenOn: appreciationGivenOn || new Date().toISOString().split("T")[0],
      visibility: appreciationVisibility,
      summary: appreciationSummary,
      createdAt: new Date().toISOString().split("T")[0],
      updatedAt: new Date().toISOString().split("T")[0],
    };
    setAppreciations(prev => [newAppr, ...prev]);
    toast.success("Appreciation added successfully!");
    setAppreciationAwardType("");
    setAppreciationRecognizedBy("");
    setAppreciationGivenOn("");
    setAppreciationSummary("");
    setAppreciationVisibility("Public");
    setShowAddAppreciationModal(false);
  };

  const handleAddAppraisalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const oldCtcVal = parseFloat(appraisalOldCtc) || 0;
    const newCtcVal = parseFloat(appraisalNewCtc) || 0;
    const incAmount = newCtcVal - oldCtcVal;
    const incPercent = oldCtcVal > 0 ? ((incAmount / oldCtcVal) * 100).toFixed(1) : "0";

    const newAppr = {
      id: "mock-appraisal-" + Date.now(),
      newDesignation: appraisalNewDesignation || "Senior Software Engineer",
      oldCtc: appraisalOldCtc || "0",
      newCtc: appraisalNewCtc || "0",
      incrementAmount: incAmount.toFixed(0),
      incrementPercentage: incPercent,
      effectiveDate: appraisalEffectiveDate,
      remarks: appraisalRemarks,
    };
    setAppraisals(prev => [newAppr, ...prev]);
    toast.success("Appraisal added successfully!");
    setAppraisalNewDesignation("");
    setAppraisalOldCtc("");
    setAppraisalNewCtc("");
    setAppraisalRemarks("");
    setShowAddAppraisalModal(false);
  };

  const handleAddRelegationSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const oldCtcVal = parseFloat(relegationOldCtc) || 0;
    const newCtcVal = parseFloat(relegationNewCtc) || 0;
    const decAmount = oldCtcVal - newCtcVal;
    const decPercent = oldCtcVal > 0 ? ((decAmount / oldCtcVal) * 100).toFixed(1) : "0";

    const newRelegation = {
      id: "mock-relegation-" + Date.now(),
      newDesignation: relegationNewDesignation || "Junior Software Engineer",
      oldCtc: relegationOldCtc || "0",
      newCtc: relegationNewCtc || "0",
      decrementAmount: decAmount.toFixed(0),
      decrementPercentage: decPercent,
      effectiveDate: relegationEffectiveDate,
      remarks: relegationRemarks,
    };
    setRelegations(prev => [newRelegation, ...prev]);
    toast.success("Relegation added successfully!");
    setRelegationNewDesignation("");
    setRelegationOldCtc("");
    setRelegationNewCtc("");
    setRelegationRemarks("");
    setShowAddRelegationModal(false);
  };

  const handleAddFineWarningSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newFine = {
      id: "mock-fine-" + Date.now(),
      type: fineType,
      amount: fineType === "FINE" ? fineAmount : "-",
      date: fineDate,
      reason: fineReason,
    };
    setFinesWarnings(prev => [newFine, ...prev]);
    toast.success(`${fineType === "FINE" ? "Fine" : "Warning"} added successfully!`);
    setFineAmount("");
    setFineDate("");
    setFineReason("");
    setShowAddFineWarningModal(false);
  };

  const handleAddRewardSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newReward = {
      id: "mock-reward-" + Date.now(),
      name: rewardIsMonetary ? `Monetary Reward (₹ ${rewardAmount})` : "Non-Monetary Reward",
      amount: rewardIsMonetary ? rewardAmount : "-",
      date: rewardGivenOn,
      reason: rewardDescription,
    };
    setRewards(prev => [newReward, ...prev]);
    toast.success("Reward added successfully!");
    setRewardAmount("");
    setRewardDescription("");
    setRewardIsMonetary(false);
    setShowAddRewardModal(false);
  };

  const handleResignTerminateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const info = {
      type: resignTerminateActionType,
      date: resignTerminateDate,
      reason: resignTerminateReason,
      finalDay: resignTerminateFinalDay,
    };
    setResignTerminateInfo(info);
    toast.success(`${resignTerminateActionType === "RESIGN" ? "Resignation initiated" : "Termination finalized"} successfully!`);
    setShowResignTerminateModal(false);
  };

  useEffect(() => {
    if (activeTab === "Leaves" && id) loadLeaveData();
  }, [activeTab, id, loadLeaveData]);

  useEffect(() => {
    async function loadOptions() {
      if (!showNewLeaveTypeModal) return;
      try {
        const [depRes, desRes] = await Promise.all([
          fetchHrDepartments(1, 100),
          fetchHrDesignations(1, 100),
        ]);
        if (depRes.success && depRes.data) {
          const list = Array.isArray(depRes.data) ? depRes.data : depRes.data.departments || [];
          setDepartmentsList(list);
        }
        if (desRes.success && desRes.data) {
          const list = Array.isArray(desRes.data) ? desRes.data : desRes.data.designations || [];
          setDesignationsList(list);
        }
        if (raw.companyId && raw.branchId) {
          const roleRes = await fetchHrRoles(raw.companyId, raw.branchId);
          if (roleRes.success && roleRes.data) {
            setRolesList(roleRes.data);
          }
        }
      } catch (err) {
        console.error("Error loading applicability options:", err);
      }
    }
    loadOptions();
  }, [showNewLeaveTypeModal, raw.companyId, raw.branchId]);

  const handleSubmitLeave = async () => {
    if (!leaveFormDate) { toast.error("Please select a date"); return; }
    if (!leaveFormLeaveTypeId) { toast.error("Please select a leave type"); return; }
    if (!leaveFormReason.trim()) { toast.error("Reason is required"); return; }

    setIsLeaveSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("employeeId", id!);
      formData.append("leaveTypeId", leaveFormLeaveTypeId);
      formData.append("leaveMode", leaveFormType);
      formData.append("startDate", leaveFormDate);
      if (leaveFormType === "MULTIPLE" && leaveFormEndDate) {
        formData.append("endDate", leaveFormEndDate);
      }
      formData.append("reason", leaveFormReason.trim());
      if (leaveFormDocument) {
        formData.append("documentUrls", leaveFormDocument);
      }

      const res = await createHrLeaveRequest(formData);
      if (res.success) {
        toast.success("Leave request created successfully!");
        setShowAddLeaveModal(false);
        resetLeaveForm();
        loadLeaveData();
      } else {
        toast.error(res.message || "Failed to create leave request");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to create leave request");
    } finally {
      setIsLeaveSubmitting(false);
    }
  };

  const resetLeaveForm = () => {
    setLeaveFormType("FULL_DAY");
    setLeaveFormDate("");
    setLeaveFormEndDate("");
    setLeaveFormLeaveTypeId("");
    setLeaveFormStatus("PENDING");
    setLeaveFormReason("");
    setLeaveFormDocument(null);
  };

  const handleDeleteLeave = async (leaveId: string) => {
    try {
      const res = await deleteHrLeaveRequest(leaveId);
      if (res.success) {
        toast.success("Leave request deleted");
        loadLeaveData();
      } else {
        toast.error("Failed to delete leave request");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete");
    }
  };

  // Handle default selection on load
  useEffect(() => {
    if (!isLoading && employees.length > 0 && !id) {
      navigate(`/hr/employee/${employees[0].id}`, { replace: true });
    }
  }, [isLoading, employees, id, navigate]);

  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return employees;
    const query = searchQuery.toLowerCase();
    return employees.filter((emp) =>
      [emp.name, emp.empId, emp.designation]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [employees, searchQuery]);



  const personalInfoFields = [
    {
      label: "ID",
      value: emp.empId || "-",
      icon: <IconId size={16} className="text-gray-400" />,
    },
    {
      label: "User Role",
      value: emp.designation || "-",
      icon: <IconLock size={16} className="text-gray-400" />,
    },
    {
      label: "Working From",
      value: raw.workingFrom || "Home",
      icon: <IconBriefcase size={16} className="text-gray-400" />,
    },
    {
      label: "Work Email",
      value: emp.email || "-",
      icon: <IconMail size={16} className="text-gray-400" />,
    },
    {
      label: "Department",
      value: raw.departmentName || "IT & Support",
      icon: <IconCategory size={16} className="text-gray-400" />,
    },
    {
      label: "Employment Type",
      value: raw.employmentType || "Full Time",
      icon: <IconUsers size={16} className="text-gray-400" />,
    },
    {
      label: "Name",
      value: emp.name || "-",
      icon: <IconUser size={16} className="text-gray-400" />,
    },
    {
      label: "Designation",
      value: emp.designation || "-",
      icon: <IconCertificate size={16} className="text-gray-400" />,
    },
    {
      label: "Aadhar Card",
      value: raw.aadharNumber || "-",
      icon: <IconFileText size={16} className="text-gray-400" />,
    },
    {
      label: "Mobile",
      value: emp.phone || "-",
      icon: <IconPhone size={16} className="text-gray-400" />,
    },
    {
      label: "Gender",
      value: raw.gender || "male",
      icon: <IconUser size={16} className="text-gray-400" />,
    },
    {
      label: "Pan Card Number",
      value: raw.panNumber || "-",
      icon: <IconCreditCard size={16} className="text-gray-400" />,
    },
    {
      label: "Skills",
      value: raw.skills || "-",
      icon: <IconBracketsAngle size={16} className="text-gray-400" />,
    },
    {
      label: "Date of Birth",
      value: raw.dateOfBirth
        ? new Date(raw.dateOfBirth).toLocaleDateString()
        : "05-06-2008",
      icon: <IconCalendarEvent size={16} className="text-gray-400" />,
    },
    {
      label: "UAN Number",
      value: raw.uanNumber || "-",
      icon: <IconCreditCard size={16} className="text-gray-400" />,
    },
    {
      label: "Current work",
      value: raw.currentWork || "-",
      icon: <IconBriefcase size={16} className="text-gray-400" />,
    },
    {
      label: "Blood Group",
      value: raw.bloodGroup || "-",
      icon: <IconShield size={16} className="text-gray-400" />,
    },
    {
      label: "Attendance Type",
      value: raw.attendanceType || "normal",
      icon: <IconFingerprint size={16} className="text-gray-400" />,
    },
    {
      label: "Years of Experience",
      value: raw.experienceYears || "-",
      icon: <IconListDetails size={16} className="text-gray-400" />,
    },
    {
      label: "Past Work",
      value: raw.pastWork || "-",
      icon: <IconBriefcase size={16} className="text-gray-400" />,
    },
    {
      label: "Gate No",
      value: raw.gateNo || "-",
      icon: <IconDeviceLaptop size={16} className="text-gray-400" />,
    },
    {
      label: "Location",
      value: raw.location || "-",
      icon: <IconMapPin size={16} className="text-gray-400" />,
    },
    {
      label: "Current Address",
      value: raw.currentAddress || "-",
      icon: <IconMapPin size={16} className="text-gray-400" />,
    },
    {
      label: "Permanent Address",
      value: raw.permanentAddress || "-",
      icon: <IconMapPin size={16} className="text-gray-400" />,
    },
    {
      label: "Salary Cycle",
      value: raw.salaryCycle || "Monthly",
      icon: <IconClock size={16} className="text-gray-400" />,
    },
  ];

  return (
    <div className="p-6 space-y-4 pb-12">
      {/* ─── Back Button ───────────────────────────────────────────── */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/hr?tab=Employee")}
          className="w-9 h-9 flex items-center justify-center bg-white border border-slate-200 rounded-full hover:border-[#FBD037] hover:bg-[#FBD037]/10 transition shadow-xs cursor-pointer text-slate-700"
          title="Back to Employees List"
        >
          <IconArrowLeft size={18} stroke={2.5} />
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <svg
            className="animate-spin h-8 w-8 text-[#FBD037]"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
        </div>
      ) : (
        /* Merged Layout Container */
        <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm flex flex-col lg:flex-row overflow-hidden min-h-[500px]">
          {/* Left Pane: Employee Master List */}
          <div className="w-full lg:w-[280px] bg-slate-50/50 border-r border-slate-150/60 p-4 flex flex-col flex-shrink-0">
            <div className="flex items-center gap-2 mb-3 px-1">
              <IconUsers size={16} className="text-slate-500" />
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Employees</h3>
            </div>
            
            <div className="relative mb-3">
              <IconSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037]/20 rounded-xl outline-none transition-all duration-200"
              />
            </div>

            <div className="space-y-1 overflow-y-auto max-h-[480px] pr-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-thumb]:rounded-full">
              {filteredEmployees.map((e) => {
                const isActive = e.id === id;
                const initials = e.name
                  .split(" ")
                  .map((n: string) => n[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase();
                return (
                  <div
                    key={e.id}
                    onClick={() => navigate(`/hr/employee/${e.id}`)}
                    className={`flex items-center gap-3 p-2 rounded-xl cursor-pointer transition-all duration-150 ${
                      isActive
                        ? "bg-[#FBD037]/10 text-slate-900 font-semibold"
                        : "hover:bg-slate-100/70 text-slate-700"
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 transition-colors ${
                      isActive ? "bg-[#FBD037] text-[#131327]" : "bg-slate-200/70 text-slate-500"
                    }`}>
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-bold text-gray-800 truncate">{e.name}</h4>
                      <p className="text-[9px] text-gray-500 truncate mt-0.5">{e.designation}</p>
                    </div>
                  </div>
                );
              })}
              {filteredEmployees.length === 0 && (
                <div className="text-center text-xs text-gray-400 py-6">
                  No employees found
                </div>
              )}
            </div>
          </div>

          {/* Right Pane: Tabs & Content */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Tabs Header inside the Card */}
            <div className="border-b border-slate-150/60 bg-slate-50/20 px-6 py-2.5 flex items-center overflow-x-auto hide-scrollbar gap-1.5">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.label;
                return (
                  <button
                    key={tab.label}
                    onClick={() => setActiveTab(tab.label)}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition cursor-pointer ${
                      isActive
                        ? "bg-[#FBD037] text-[#131327] shadow-xs"
                        : "bg-transparent text-slate-550 hover:text-slate-800 hover:bg-slate-100/50"
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab Content Body */}
            <div className="p-6 flex-1 overflow-y-auto">
              {activeTab === "Personal Info" && (
                <div className="space-y-6 flex flex-col">
                  {/* Profile Header */}
                  <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                    <div className="flex items-center gap-2">
                      <IconUser className="text-slate-500" size={16} />
                      <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                        Profile Information
                      </h2>
                      <span className="text-[8px] font-bold text-[#131327] bg-[#FBD037] px-1.5 py-0.5 rounded uppercase tracking-wider">
                        Active
                      </span>
                    </div>
                    <button className="flex items-center gap-1 px-2.5 py-1 border border-slate-200 hover:border-slate-350 hover:bg-slate-50 rounded-lg text-[10px] font-bold text-slate-600 transition shadow-xs cursor-pointer">
                      <IconEdit size={12} />
                      Edit
                    </button>
                  </div>

                  {/* Profile fields grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-4">
                    {personalInfoFields.map((f, i) => (
                      <div key={i} className="space-y-1 animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                          {f.label}
                        </div>
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                          <span className="text-slate-400">{f.icon}</span>
                          <span className="truncate">{f.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Sub-cards */}
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 pt-4 border-t border-slate-100">
                    {/* Personal Subcard */}
                    <div className="bg-[#FCFDF8] rounded-xl border border-slate-200/50 shadow-inner p-4 space-y-3">
                      <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                        <IconUser size={14} className="text-slate-500" />
                        Personal Details
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { label: "Personal Email", value: raw.personalEmail },
                          { label: "Marital Status", value: raw.maritalStatus },
                          { label: "Nationality", value: raw.nationality },
                          { label: "Address", value: raw.presentAddress }
                        ].map((item, index) => (
                          <div key={index} className="space-y-0.5">
                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{item.label}</div>
                            <div className="text-[11px] font-semibold text-slate-700 truncate">{item.value || "-"}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Family Subcard */}
                    <div className="bg-[#FCFDF8] rounded-xl border border-slate-200/50 shadow-inner p-4 flex flex-col justify-between">
                      <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-wider border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                        <IconUsers size={14} className="text-slate-500" />
                        Family Details (0)
                      </h3>
                      <div className="flex-1 flex flex-col justify-center items-center text-center py-4 text-slate-400">
                        <h4 className="text-[11px] font-bold text-slate-550">No details added</h4>
                        <p className="text-[9px] text-slate-400 mt-0.5 leading-relaxed">No dependency records found.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "Work" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in fade-in duration-200">
                  
                  {/* Card 1: Employment Details */}
                  <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                      <IconBriefcase className="text-slate-500" size={16} />
                      <h3 className="text-xs font-bold text-[#212121] uppercase tracking-wider">Employment Details</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-4 text-xs">
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Joining Date</div>
                        <div className="font-semibold text-slate-750 mt-1 flex items-center gap-1.5">
                          <IconCalendar size={14} className="text-gray-400" />
                          {emp.joiningDate || raw.joiningDate || "22-07-2026"}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Department</div>
                        <div className="font-semibold text-slate-750 mt-1">
                          {emp.departmentName || raw.department?.departmentName || "IT & Support"}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Work Address</div>
                        <div className="font-semibold text-slate-750 mt-1 flex items-start gap-1">
                          <IconMapPin size={14} className="text-gray-400 shrink-0 mt-0.5" />
                          <span>{raw.workAddress || "5 Star - Chenani"}</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Probation Period Duration Time</div>
                        <div className="font-semibold text-slate-750 mt-1 flex items-center gap-1.5">
                          <IconClock size={14} className="text-gray-400" />
                          {raw.probationDuration || "30 day"}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Probation Period</div>
                        <div className="font-semibold text-slate-750 mt-1 flex items-center gap-1.5">
                          <IconClock size={14} className="text-gray-400" />
                          {raw.probationPeriod || "Yes"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Reporting & Compensation */}
                  <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
                    <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                      <IconUser className="text-slate-500" size={16} />
                      <h3 className="text-xs font-bold text-[#212121] uppercase tracking-wider">Reporting & Compensation</h3>
                    </div>
                    <div className="space-y-4 text-xs">
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Reports To</div>
                        <div className="flex items-center gap-3 mt-1.5">
                          <div className="w-9 h-9 rounded-full bg-slate-200 border border-slate-300 flex items-center justify-center font-bold text-slate-700 text-sm">
                            {(raw.reportsTo || "Sanju").charAt(0)}
                          </div>
                          <div>
                            <div className="font-semibold text-slate-750">{raw.reportsTo || "Sanju"}</div>
                            <div className="text-[10px] text-gray-400">IT & Support</div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Salary</div>
                        <div className="font-bold text-slate-800 text-base mt-1">
                          ₹ {raw.salary || emp.salary || "30000"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Bank Details */}
                  <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                      <div className="flex items-center gap-2">
                        <IconCreditCard className="text-slate-500" size={16} />
                        <h3 className="text-xs font-bold text-[#212121] uppercase tracking-wider">Bank Details</h3>
                      </div>
                      <button 
                        onClick={openEditBankModal}
                        className="px-3.5 py-1.5 bg-[#212121] hover:bg-black text-white text-[10px] font-bold rounded-lg transition cursor-pointer flex items-center gap-1.5"
                      >
                        <IconEdit size={12} /> Edit Bank Details
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-4 text-xs">
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Account Holder Name</div>
                        <div className="font-semibold text-slate-750 mt-1">{raw.accountHolderName || "Test Test"}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Account Number</div>
                        <div className="font-semibold text-slate-750 mt-1">{raw.accountNumber || "21245151561515315"}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Account Type</div>
                        <div className="font-semibold text-slate-750 mt-1">{raw.accountType || "Savings Account"}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Bank Name</div>
                        <div className="font-semibold text-slate-750 mt-1">{raw.bankName || "NATIONAL BANK"}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">IFSC Code</div>
                        <div className="font-semibold text-slate-750 mt-1">{raw.ifscCode || "KKBK0000387"}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Branch Name</div>
                        <div className="font-semibold text-slate-750 mt-1">{raw.branchName || "NATION"}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Branch Address</div>
                        <div className="font-semibold text-slate-750 mt-1">{raw.branchAddress || "12312, NATIONAL NATION,"}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-gray-400 font-bold uppercase text-[9px] tracking-wider">Verified by</div>
                        <button 
                          onClick={toggleBankVerification}
                          className={`mt-1.5 px-3 py-1.5 border rounded-lg text-[10px] font-bold transition flex items-center gap-1.5 cursor-pointer bg-white ${
                            raw.isBankVerified 
                              ? "border-green-200 text-green-600 bg-green-50/20" 
                              : "border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <IconCheck size={12} className={raw.isBankVerified ? "text-green-600" : "text-slate-400"} />
                          {raw.isBankVerified ? "Verified" : "Mark as verified"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Card 4: Assigned Assets */}
                  <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                      <div className="flex items-center gap-2">
                        <IconDeviceLaptop className="text-slate-500" size={16} />
                        <h3 className="text-xs font-bold text-[#212121] uppercase tracking-wider">Assigned Assets</h3>
                      </div>
                      <button 
                        onClick={() => setShowRequestsModal(true)}
                        className="px-3.5 py-1.5 bg-[#212121] hover:bg-black text-white text-[10px] font-bold rounded-lg transition cursor-pointer flex items-center gap-1"
                      >
                        <IconPlus size={12} /> Request
                      </button>
                    </div>
                    {assetRequests.length === 0 ? (
                      <div className="py-12 text-center flex flex-col justify-center items-center">
                        <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-3">
                          <IconFileText size={28} className="text-slate-300" />
                        </div>
                        <h3 className="text-xs font-bold text-slate-700">No Data Found</h3>
                        <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                          We couldn't find any data to show here.
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-xs">
                          <thead>
                            <tr className="border-b border-slate-100 font-bold text-gray-500">
                              <th className="py-2">Asset</th>
                              <th className="py-2">Return Date</th>
                              <th className="py-2">Type</th>
                              <th className="py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50 font-medium">
                            {assetRequests.map((req: any, idx) => (
                              <tr key={idx} className="text-slate-700">
                                <td className="py-2.5 font-semibold text-[#212121]">{req.asset}</td>
                                <td className="py-2.5 text-gray-500">{req.returnDate || "-"}</td>
                                <td className="py-2.5">
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${req.type === "LENT" ? "bg-blue-50 text-blue-600" : "bg-orange-50 text-orange-600"}`}>
                                    {req.type}
                                  </span>
                                </td>
                                <td className="py-2.5">
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-600`}>
                                    {req.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                </div>
              )}

              {activeTab === "Documents" && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <div className="flex items-center gap-2">
                      <IconFileText className="text-slate-500" size={16} />
                      <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Documents</h2>
                    </div>
                    <button
                      onClick={() => {
                        setDocModalMode("ADD");
                        setSelectedDocForEdit(null);
                        setUploadDocType("");
                        setUploadDocFile(null);
                        setShowUploadDocModal(true);
                      }}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#212121] hover:bg-[#333] text-white text-[11px] font-bold rounded-lg transition cursor-pointer"
                    >
                      <IconUpload size={14} /> Upload Document
                    </button>
                  </div>
                  {isDocsLoading || isEmployeeDetailsLoading ? (
                    <div className="py-12 text-center text-xs text-slate-400 animate-pulse">Loading documents...</div>
                  ) : employeeDocuments.length === 0 ? (
                    <div className="py-12 text-center flex flex-col justify-center items-center">
                      <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-3">
                        <IconFileText size={28} className="text-slate-300" />
                      </div>
                      <h3 className="text-xs font-bold text-slate-700">No documents uploaded</h3>
                      <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                        Click the Upload Document button to add your first document
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
                      {employeeDocuments.map((doc, idx) => {
                        const isVerified = doc.raw?.isVerified || false;
                        const dateStr = doc.raw?.uploadedAt 
                          ? new Date(doc.raw.uploadedAt).toLocaleDateString("en-US", { day: 'numeric', month: 'short' })
                          : "14 Jul"; // fallback

                        const handleView = () => {
                          if (doc.url) {
                            window.open(doc.url.startsWith("http") ? doc.url : `http://${doc.url}`, "_blank");
                          } else {
                            toast.success(`Mock view for ${doc.name}`);
                          }
                        };

                        return (
                          <div 
                            key={idx} 
                            className="relative bg-[#F8F9FA] rounded-2xl border border-slate-200/80 p-6 flex flex-col items-center justify-center text-center shadow-xs transition-all duration-200 hover:shadow-md hover:border-slate-350 w-full max-w-[240px] mx-auto group"
                          >
                            {/* Delete button (top right) */}
                            <button
                              onClick={async () => {
                                if (doc.id.startsWith("mock-")) {
                                  setEmployeeDocuments(prev => prev.filter(d => d.id !== doc.id));
                                  toast.success("Document removed successfully");
                                  return;
                                }
                                if (!window.confirm(`Are you sure you want to delete "${doc.name}"?`)) return;

                                const loader = toast.loading("Deleting document...");
                                try {
                                  const res = await deleteHrEmployeeDocument(id!, doc.id);
                                  if (res.success) {
                                    toast.success("Document deleted successfully!", { id: loader });
                                    loadEmployeeDetails();
                                  } else {
                                    toast.error(res.message || "Failed to delete document", { id: loader });
                                  }
                                } catch (err: any) {
                                  toast.error(err.message || "Failed to delete document", { id: loader });
                                }
                              }}
                              className="absolute top-3.5 right-3.5 text-red-500 hover:text-red-700 transition p-1 cursor-pointer bg-transparent border-none outline-none"
                              title="Delete Document"
                            >
                              <IconTrash size={16} />
                            </button>

                            {/* File Icon Box - Clickable */}
                            <div 
                              onClick={handleView}
                              className="w-14 h-14 bg-white border border-slate-150 rounded-2xl flex items-center justify-center text-slate-400 mb-4 cursor-pointer hover:border-blue-455 hover:scale-105 transition-all shadow-xs"
                              title="Click to view document"
                            >
                              <IconFileText size={24} className="text-slate-600" />
                            </div>

                            {/* Badge */}
                            <span 
                              className={`text-[9px] font-extrabold px-3 py-0.5 rounded-md mb-3.5 uppercase tracking-wider text-white ${
                                isVerified 
                                  ? "bg-[#10B981]" 
                                  : "bg-[#F59E0B]"
                              }`}
                            >
                              {isVerified ? "Verified" : "Pending"}
                            </span>

                            {/* Title & Name */}
                            <h4 className="text-xs font-bold text-slate-800 line-clamp-1 max-w-full">
                              {doc.type}
                            </h4>
                            <p className="text-[10px] text-slate-400 mt-1.5 font-semibold">
                              Uploaded: {dateStr}
                            </p>

                            {/* Actions Row */}
                            <div className="flex items-center gap-4 mt-5 pt-3 w-full justify-center border-t border-slate-100">
                              {/* View/Download */}
                              <button
                                onClick={handleView}
                                className="p-1 text-[#3B82F6] hover:scale-115 transition bg-transparent border-none cursor-pointer outline-none"
                                title="Download / View"
                              >
                                <IconDownload size={16} />
                              </button>

                              {/* Verify Action */}
                              <button
                                onClick={async () => {
                                  const loader = toast.loading("Verifying document...");
                                  try {
                                    const res = await verifyHrEmployeeDocument(id!, doc.id, true);
                                    if (res.success) {
                                      toast.success("Document verified successfully!", { id: loader });
                                      loadEmployeeDetails();
                                    } else {
                                      toast.error(res.message || "Failed to verify document", { id: loader });
                                    }
                                  } catch (err: any) {
                                    toast.error(err.message || "Failed to verify document", { id: loader });
                                  }
                                }}
                                className="p-1 text-[#10B981] hover:scale-115 transition bg-transparent border-none cursor-pointer outline-none"
                                title="Verify Document"
                              >
                                <IconCheck size={16} />
                              </button>

                              {/* Reject/Unverify Action */}
                              <button
                                onClick={async () => {
                                  const loader = toast.loading("Rejecting document...");
                                  try {
                                    const res = await verifyHrEmployeeDocument(id!, doc.id, false);
                                    if (res.success) {
                                      toast.success("Document set to pending!", { id: loader });
                                      loadEmployeeDetails();
                                    } else {
                                      toast.error(res.message || "Failed to reject document", { id: loader });
                                    }
                                  } catch (err: any) {
                                    toast.error(err.message || "Failed to reject document", { id: loader });
                                  }
                                }}
                                className="p-1 text-[#EF4444] hover:scale-115 transition bg-transparent border-none cursor-pointer outline-none"
                                title="Reject / Pending"
                              >
                                <IconCircleX size={16} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ── LEAVES TAB ──────────────────────────────── */}
              {activeTab === "Leaves" && (
                <div className="space-y-6">
                  {/* Stat Cards */}
                  {(() => {
                    const taken = leaveRequests.length;
                    const pending = leaveRequests.filter(l => l.status === "PENDING").length;
                    const approved = leaveRequests.filter(l => l.status === "APPROVED").length;
                    const rejected = leaveRequests.filter(l => l.status === "REJECTED").length;
                    const withoutApproval = leaveRequests.filter(l => !l.status || l.status === "WITHOUT_APPROVAL").length;
                    const stats = [
                      { label: "Taken Leaves", value: taken, icon: <IconCalendarEvent size={20} className="text-slate-400" />, color: "text-slate-800" },
                      { label: "Pending Approval", value: pending, icon: <IconClockPause size={20} className="text-amber-400" />, color: "text-amber-500" },
                      { label: "Approved Leaves", value: approved, icon: <IconClipboardCheck size={20} className="text-emerald-400" />, color: "text-emerald-500" },
                      { label: "Rejected Leaves", value: rejected, icon: <IconClipboardX size={20} className="text-red-400" />, color: "text-red-500" },
                      { label: "Without Approval", value: withoutApproval, icon: <IconAlertCircle size={20} className="text-orange-400" />, color: "text-orange-500" },
                    ];
                    return (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {stats.map((s, i) => (
                          <div key={i} className="bg-white border border-slate-100 rounded-xl p-3 flex items-center justify-between shadow-xs">
                            <div>
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{s.label}</p>
                              <p className={`text-xl font-extrabold mt-0.5 ${s.color}`}>{s.value}</p>
                            </div>
                            {s.icon}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Leave Types Overview */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-bold text-slate-800">Leave Types Overview</h3>
                    {isLeavesLoading ? (
                      <div className="py-6 text-center text-xs text-slate-400 animate-pulse">Loading leave types...</div>
                    ) : leaveTypes.length === 0 ? (
                      <div className="py-6 text-center text-xs text-slate-400">No leave types configured</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {leaveTypes.map((lt: any) => {
                          const empLeaves = leaveRequests.filter((l: any) => l.leaveTypeId === (lt.id || lt._id));
                          const used = empLeaves.filter((l: any) => l.status === "APPROVED").length;
                          const allocated = lt.noOfLeaves !== undefined ? lt.noOfLeaves : (lt.allowedLeaves || lt.totalLeaves || 0);
                          const remaining = typeof allocated === "number" ? allocated - used : 0;
                          const isEditing = editingLeaveTypeId === (lt.id || lt._id);

                          return (
                            <div key={lt.id || lt._id} className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-2 relative">
                              {/* Header & Edit Icons */}
                              <div className="flex items-center justify-between border-b border-slate-100/50 pb-1.5">
                                <h4 className="text-xs font-bold text-slate-700">{lt.leaveType || lt.name || lt.leaveTypeName || "Leave"}</h4>
                                {isEditing ? (
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      disabled={isLeaveTypeSaving}
                                      onClick={() => setEditingLeaveTypeId(null)}
                                      className="p-1 rounded text-slate-400 hover:text-red-500 transition cursor-pointer bg-transparent border-none outline-none"
                                      title="Cancel"
                                    >
                                      <IconX size={14} />
                                    </button>
                                    <button
                                      disabled={isLeaveTypeSaving}
                                      onClick={async () => {
                                        setIsLeaveTypeSaving(true);
                                        const loader = toast.loading("Saving configuration...");
                                        try {
                                          const res = await updateHrLeaveType(lt.id || lt._id, {
                                            leaveTypeName: lt.name || lt.leaveTypeName,
                                            noOfLeaves: Number(editAllocated),
                                            monthlyLimit: Number(editMonthlyLimit)
                                          });
                                          if (res.success) {
                                            toast.success("Leave type updated successfully!", { id: loader });
                                            setEditingLeaveTypeId(null);
                                            loadLeaveData();
                                          } else {
                                            toast.error(res.message || "Failed to update leave type", { id: loader });
                                          }
                                        } catch (err: any) {
                                          toast.error(err.message || "Failed to update leave type", { id: loader });
                                        } finally {
                                          setIsLeaveTypeSaving(false);
                                        }
                                      }}
                                      className="p-1 rounded text-emerald-500 hover:text-emerald-700 transition cursor-pointer bg-transparent border-none outline-none"
                                      title="Save"
                                    >
                                      <IconDeviceFloppy size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setEditingLeaveTypeId(lt.id || lt._id);
                                      setEditAllocated(String(allocated));
                                      setEditMonthlyLimit(String(lt.monthlyLimit || 0));
                                    }}
                                    className="p-1 rounded text-blue-500 hover:text-blue-750 transition cursor-pointer bg-transparent border-none outline-none"
                                    title="Edit Config"
                                  >
                                    <IconEdit size={14} />
                                  </button>
                                )}
                              </div>

                              {/* Details */}
                              <div className="grid grid-cols-2 gap-y-1.5 text-[11px] items-center">
                                <span className="text-slate-400 font-semibold">Allocated:</span>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editAllocated}
                                    onChange={(e) => setEditAllocated(e.target.value)}
                                    className="w-16 rounded border border-slate-200 px-1 py-0.5 text-right font-semibold text-slate-700 bg-white outline-none focus:border-blue-400"
                                  />
                                ) : (
                                  <span className="text-slate-700 font-bold text-right">{allocated}</span>
                                )}

                                <span className="text-slate-400 font-semibold">Used:</span>
                                <span className="text-slate-700 font-bold text-right">{used}</span>

                                <span className="text-emerald-500 font-semibold">Remaining:</span>
                                <span className="text-emerald-650 font-bold text-right">{remaining}</span>

                                <span className="text-slate-400 font-semibold">Monthly Limit:</span>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    step="0.5"
                                    value={editMonthlyLimit}
                                    onChange={(e) => setEditMonthlyLimit(e.target.value)}
                                    className="w-16 rounded border border-slate-200 px-1 py-0.5 text-right font-semibold text-slate-700 bg-white outline-none focus:border-blue-400"
                                  />
                                ) : (
                                  <span className="text-[#F59E0B] font-bold text-right">{lt.monthlyLimit || "-"}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Leave History Table */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-slate-800">Leave History</h3>
                      <button
                        onClick={() => { resetLeaveForm(); setShowAddLeaveModal(true); }}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#212121] hover:bg-[#333] text-white text-[11px] font-bold rounded-lg transition cursor-pointer animate-in fade-in"
                      >
                        <IconPlus size={14} /> Leave Request
                      </button>
                    </div>

                    <div className="border border-slate-100 rounded-xl overflow-hidden bg-white shadow-xs">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            <th className="py-3 px-3 text-left">SR.NO</th>
                            <th className="py-3 px-3 text-left">EMPLOYEE NAME</th>
                            <th className="py-3 px-3 text-left">LEAVE PERIOD</th>
                            <th className="py-3 px-3 text-left">TYPE</th>
                            <th className="py-3 px-3 text-left">APPLIED DATE</th>
                            <th className="py-3 px-3 text-left">STATUS</th>
                            <th className="py-3 px-3 text-center">ACTIONS</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {isLeavesLoading ? (
                            <tr><td colSpan={7} className="py-10 text-center text-slate-400 animate-pulse">Loading...</td></tr>
                          ) : leaveRequests.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="py-12 text-center">
                                <div className="space-y-2">
                                  <div className="w-12 h-12 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-center mx-auto">
                                    <IconPlaneDeparture size={20} className="text-slate-300" />
                                  </div>
                                  <p className="text-xs font-bold text-slate-600">No Leaves data found</p>
                                  <p className="text-[10px] text-slate-400">We couldn't find any data to show here.</p>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            leaveRequests.map((leave: any, idx: number) => {
                              const leaveTypeName = leaveTypes.find((t: any) => (t.id || t._id) === leave.leaveTypeId)?.leaveType || leaveTypes.find((t: any) => (t.id || t._id) === leave.leaveTypeId)?.name || leave.leaveTypeName || "-";
                              const statusColor = leave.status === "APPROVED" ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                                : leave.status === "REJECTED" ? "bg-red-50 text-red-500 border-red-200"
                                : "bg-amber-50 text-amber-600 border-amber-200";
                              return (
                                <tr key={leave.id || leave._id || idx} className="hover:bg-slate-50/50 transition">
                                  <td className="py-3 px-3 font-mono text-slate-400 font-semibold">{idx + 1}</td>
                                  <td className="py-3 px-3 font-semibold text-slate-700">{emp.name || "-"}</td>
                                  <td className="py-3 px-3 text-slate-600">
                                    {leave.leaveDate ? new Date(leave.leaveDate).toLocaleDateString() : "-"}
                                    {leave.leaveDateEnd ? ` - ${new Date(leave.leaveDateEnd).toLocaleDateString()}` : ""}
                                  </td>
                                  <td className="py-3 px-3 text-slate-600">{leaveTypeName}</td>
                                  <td className="py-3 px-3 text-slate-500">{leave.createdAt ? new Date(leave.createdAt).toLocaleDateString() : "-"}</td>
                                  <td className="py-3 px-3">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase border ${statusColor}`}>
                                      {leave.status || "PENDING"}
                                    </span>
                                  </td>
                                  <td className="py-3 px-3 text-center relative">
                                    <div className="relative inline-block text-left">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActivePopoverLeaveId(
                                            activePopoverLeaveId === (leave.id || leave._id) ? null : (leave.id || leave._id)
                                          );
                                        }}
                                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition cursor-pointer outline-none bg-transparent border-none"
                                        title="Actions"
                                      >
                                        <IconDots size={16} />
                                      </button>
                                      {activePopoverLeaveId === (leave.id || leave._id) && (
                                        <>
                                          <div 
                                            className="fixed inset-0 z-40" 
                                            onClick={() => setActivePopoverLeaveId(null)} 
                                          />
                                          <div className="absolute right-0 mt-1 w-28 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1 text-left">
                                            <button
                                              onClick={() => {
                                                setSelectedLeaveForView(leave);
                                                setShowViewLeaveModal(true);
                                                setActivePopoverLeaveId(null);
                                              }}
                                              className="w-full px-3 py-1.5 text-slate-600 hover:bg-slate-50 transition text-[11px] font-bold flex items-center gap-2 cursor-pointer bg-transparent border-none outline-none"
                                            >
                                              <IconEye size={12} className="text-slate-400" /> View
                                            </button>
                                            <button
                                              onClick={() => {
                                                setSelectedLeaveForEdit(leave);
                                                // Load states
                                                setLeaveFormType(leave.dayType || "FULL_DAY");
                                                setLeaveFormDate(leave.leaveDate ? leave.leaveDate.split("T")[0] : "");
                                                setLeaveFormEndDate(leave.leaveDateEnd ? leave.leaveDateEnd.split("T")[0] : "");
                                                setLeaveFormLeaveTypeId(leave.leaveTypeId || "");
                                                setLeaveFormStatus(leave.status || "PENDING");
                                                setLeaveFormReason(leave.reason || "");
                                                setShowEditLeaveModal(true);
                                                setActivePopoverLeaveId(null);
                                              }}
                                              className="w-full px-3 py-1.5 text-slate-600 hover:bg-slate-50 transition text-[11px] font-bold flex items-center gap-2 cursor-pointer bg-transparent border-none outline-none"
                                            >
                                              <IconEdit size={12} className="text-slate-400" /> Edit
                                            </button>
                                            <button
                                              onClick={() => {
                                                setSelectedLeaveForDelete(leave);
                                                setShowDeleteLeaveModal(true);
                                                setActivePopoverLeaveId(null);
                                              }}
                                              className="w-full px-3 py-1.5 text-red-500 hover:bg-red-50 transition text-[11px] font-bold flex items-center gap-2 cursor-pointer bg-transparent border-none outline-none"
                                            >
                                              <IconTrash size={12} className="text-red-400" /> Archive
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>

                      {/* Pagination footer */}
                      <div className="px-4 py-3 flex items-center justify-between border-t border-slate-100 bg-white text-[10px] font-semibold text-slate-400">
                        <span>Record {leaveRequests.length > 0 ? 1 : 0}-{leaveRequests.length} of {leaveRequests.length}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-700 font-bold">1/1</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── ATTENDANCE TAB ─────────────────────────── */}
              {activeTab === "Attendance" && (
                <div className="space-y-6 animate-in fade-in duration-200">
                  
                  {/* Month & Year Selectors */}
                  <div className="flex justify-end gap-3 items-center">
                    {/* Month Dropdown */}
                    <div className="relative">
                      <select
                        value={attendanceMonth}
                        onChange={(e) => setAttendanceMonth(Number(e.target.value))}
                        className="appearance-none h-9 pl-3 pr-8 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-700 outline-none focus:ring-1 focus:ring-[#FBD037] shadow-sm cursor-pointer min-w-[120px]"
                      >
                        {[
                          "January", "February", "March", "April", "May", "June",
                          "July", "August", "September", "October", "November", "December"
                        ].map((m, idx) => (
                          <option key={idx} value={idx + 1}>{m}</option>
                        ))}
                      </select>
                      <IconChevronRight size={11} className="absolute right-3 top-3 text-gray-400 rotate-90 pointer-events-none" />
                    </div>

                    {/* Year Dropdown */}
                    <div className="relative">
                      <select
                        value={attendanceYear}
                        onChange={(e) => setAttendanceYear(Number(e.target.value))}
                        className="appearance-none h-9 pl-3 pr-8 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-700 outline-none focus:ring-1 focus:ring-[#FBD037] shadow-sm cursor-pointer min-w-[90px]"
                      >
                        {[2024, 2025, 2026, 2027, 2028].map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                      <IconChevronRight size={11} className="absolute right-3 top-3 text-gray-400 rotate-90 pointer-events-none" />
                    </div>
                  </div>

                  {/* Overview Stats Cards - Grid (2 rows of 4 cards) */}
                  {(() => {
                    const daysInMonthList = (() => {
                      const date = new Date(attendanceYear, attendanceMonth - 1, 1);
                      const list = [];
                      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
                      while (date.getMonth() === attendanceMonth - 1) {
                        list.push({
                          dateNum: date.getDate(),
                          dayName: dayNames[date.getDay()],
                          fullDateStr: `${attendanceYear}-${String(attendanceMonth).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
                        });
                        date.setDate(date.getDate() + 1);
                      }
                      return list;
                    })();

                    const totalDays = daysInMonthList.length;
                    const presentCount = attendanceData.filter((l: any) => l.status === "PRESENT" || l.status === "LATE").length;
                    const absentCount = attendanceData.filter((l: any) => l.status === "ABSENT").length;
                    const holidayCount = attendanceData.filter((l: any) => l.status === "HOLIDAY").length;
                    const lateCount = attendanceData.filter((l: any) => l.status === "LATE" || l.isLate).length;
                    const halfDayCount = attendanceData.filter((l: any) => l.status === "HALF_DAY" || l.isHalfDay).length;
                    
                    let hoursClocked = 0;
                    attendanceData.forEach((l: any) => {
                      const hrVal = l.totalWorkingHours || l.workingHours || 0;
                      hoursClocked += parseFloat(hrVal);
                    });

                    const weekOffs = attendanceData.filter((l: any) => l.status === "WEEK_OFF").length;
                    const workingDays = totalDays - weekOffs;

                    const stats = [
                      { title: "Working Days", value: workingDays, icon: <IconCalendar size={18} />, bg: "bg-blue-50 text-blue-600" },
                      { title: "Days Present", value: presentCount, icon: <IconUser size={18} />, bg: "bg-green-50 text-green-600" },
                      { title: "Days Absent", value: absentCount, icon: <IconUser size={18} />, bg: "bg-red-50 text-red-500" },
                      { title: "Holidays", value: holidayCount, icon: <IconCalendarEvent size={18} />, bg: "bg-purple-50 text-purple-600" },
                      { title: "Hours Clocked", value: hoursClocked.toFixed(1), icon: <IconClock size={18} />, bg: "bg-orange-50 text-orange-600" },
                      { title: "Late Count", value: lateCount, icon: <IconAlertCircle size={18} />, bg: "bg-pink-50 text-pink-600" },
                      { title: "Half Day", value: halfDayCount, icon: <IconRotate size={18} />, bg: "bg-indigo-50 text-indigo-600" },
                      { title: "Shift Time", value: "10:00 AM - 05:30 PM", icon: <IconBriefcase size={18} />, bg: "bg-slate-50 text-slate-600" },
                    ];

                    const monthNames = [
                      "January", "February", "March", "April", "May", "June",
                      "July", "August", "September", "October", "November", "December"
                    ];

                    return (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {stats.map((st, idx) => (
                            <div key={idx} className="bg-white rounded-3xl border border-slate-100 p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition">
                              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${st.bg}`}>
                                {st.icon}
                              </div>
                              <div>
                                <div className="text-xl font-bold text-[#212121]">{st.value}</div>
                                <div className="text-[10px] text-gray-450 font-semibold">{st.title}</div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Roster & Attendance Grid View Table */}
                        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden p-6 space-y-4">
                          <div>
                            <h3 className="text-sm font-bold text-[#212121] tracking-wide">
                              My Attendance – {monthNames[attendanceMonth - 1]} {attendanceYear}
                            </h3>
                          </div>

                          {/* Legend Row */}
                          <div className="flex flex-wrap items-center gap-4 text-[9px] font-bold text-gray-500 border-b border-dashed border-slate-100 pb-3">
                            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Present</span>
                            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Absent</span>
                            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> Late</span>
                            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Early Go</span>
                            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-purple-500" /> Holiday</span>
                            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-pink-500" /> Leave</span>
                            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-indigo-500" /> First Half Leave</span>
                            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" /> Second Half Leave</span>
                            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> Week Off</span>
                          </div>

                          {/* Horizontal scroll grid */}
                          <div className="overflow-x-auto hide-scrollbar border border-slate-100 rounded-2xl">
                            {isAttendanceLoading ? (
                              <div className="flex items-center justify-center py-16 text-gray-400 text-xs font-semibold">
                                <IconRotate size={16} className="animate-spin mr-2" /> Loading attendance logs...
                              </div>
                            ) : (
                              <table className="w-full text-left border-collapse min-w-[900px]">
                                <thead>
                                  <tr className="bg-slate-50/50 border-b border-slate-100">
                                    <th className="sticky left-0 z-10 bg-white px-5 py-4 min-w-[200px] font-bold text-xs text-[#212121] border-r border-slate-100 shadow-[1px_0_0_0_#f1f5f9]">
                                      Employee
                                    </th>
                                    {daysInMonthList.map((day, i) => (
                                      <th key={i} className="px-2.5 py-3 text-center border-r border-slate-100 last:border-r-0 min-w-[45px]">
                                        <div className="text-[10px] text-gray-450 font-bold uppercase">{day.dayName}</div>
                                        <div className="text-xs font-bold text-[#212121] mt-0.5">{day.dateNum}</div>
                                      </th>
                                    ))}
                                    <th className="px-4 py-3 font-bold text-xs text-center min-w-[60px]">
                                      TOTAL
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  <tr className="hover:bg-slate-50/30 transition-colors">
                                    {/* Left profile name */}
                                    <td className="sticky left-0 z-10 bg-white px-5 py-3 border-r border-slate-100 shadow-[1px_0_0_0_#f1f5f9] whitespace-nowrap">
                                      <div className="flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-full bg-[#FBD037]/20 border border-[#FBD037]/40 flex items-center justify-center font-bold text-[#212121] text-xs shrink-0 uppercase">
                                          {(() => {
                                            const currentEmp = employees.find((e: any) => String(e.id || e._id) === String(id));
                                            return (currentEmp?.name || currentEmp?.firstName || "E").charAt(0);
                                          })()}
                                        </div>
                                        <div>
                                          <div className="font-semibold text-xs text-[#212121]">
                                            {(() => {
                                              const currentEmp = employees.find((e: any) => String(e.id || e._id) === String(id));
                                              return currentEmp ? (currentEmp.name || `${currentEmp.firstName || ""} ${currentEmp.lastName || ""}`.trim()) : "Employee";
                                            })()}
                                          </div>
                                          <div className="text-[10px] text-gray-400 font-medium">
                                            {employees.find((e: any) => String(e.id || e._id) === String(id))?.designationName || "Staff"}
                                          </div>
                                        </div>
                                      </div>
                                    </td>

                                    {/* 1-31 Cells */}
                                    {daysInMonthList.map((day, i) => {
                                      const log = attendanceData.find((l: any) => {
                                        const dStr = l.attendanceDate || l.date || "";
                                        return dStr.split("T")[0] === day.fullDateStr;
                                      });

                                      if (!log) {
                                        return (
                                          <td key={i} className="px-2.5 py-3 text-center border-r border-slate-100 last:border-r-0 text-gray-350 font-medium text-xs">
                                            —
                                          </td>
                                        );
                                      }

                                      const status = log.status || "";
                                      
                                      // Render status icons
                                      if (status === "PRESENT") {
                                        return (
                                          <td key={i} className="px-2.5 py-3 text-center border-r border-slate-100 last:border-r-0 text-green-500">
                                            <IconCheck size={16} className="inline font-bold" />
                                          </td>
                                        );
                                      }
                                      if (status === "ABSENT") {
                                        return (
                                          <td key={i} className="px-2.5 py-3 text-center border-r border-slate-100 last:border-r-0 text-red-500">
                                            <IconCircleX size={16} className="inline font-bold" />
                                          </td>
                                        );
                                      }
                                      if (status === "LATE") {
                                        return (
                                          <td key={i} className="px-2.5 py-3 text-center border-r border-slate-100 last:border-r-0 text-blue-500">
                                            <IconClock size={16} className="inline" />
                                          </td>
                                        );
                                      }
                                      if (status === "WEEK_OFF") {
                                        return (
                                          <td key={i} className="px-2.5 py-3 text-center border-r border-slate-100 last:border-r-0 text-slate-400 text-[9px] font-bold">
                                            WO
                                          </td>
                                        );
                                      }
                                      if (status === "HOLIDAY") {
                                        return (
                                          <td key={i} className="px-2.5 py-3 text-center border-r border-slate-100 last:border-r-0 text-purple-500 text-[9px] font-bold">
                                            H
                                          </td>
                                        );
                                      }
                                      if (status === "LEAVE") {
                                        return (
                                          <td key={i} className="px-2.5 py-3 text-center border-r border-slate-100 last:border-r-0 text-pink-500 text-[9px] font-bold">
                                            L
                                          </td>
                                        );
                                      }

                                      return (
                                        <td key={i} className="px-2.5 py-3 text-center border-r border-slate-100 last:border-r-0 text-slate-400 font-semibold text-xs">
                                          {status.slice(0, 2)}
                                        </td>
                                      );
                                    })}

                                    {/* Total Count */}
                                    <td className="px-4 py-3 font-bold text-xs text-center text-[#212121] whitespace-nowrap">
                                      {presentCount}/{workingDays}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* ── ACTION TAB ────────────────────────────── */}
              {activeTab === "Action" && (
                <div className="space-y-6 animate-in fade-in duration-200">
                  
                  {/* Secondary Sub-tab Bar */}
                  <div className="border border-slate-200/80 bg-slate-50/50 rounded-2xl p-1.5 flex flex-wrap items-center gap-1">
                    {[
                      "Appreciations",
                      "Appraisal",
                      "Relegation",
                      "Fines & Warning",
                      "Rewards",
                      "Resign & Terminate"
                    ].map((subTab) => {
                      const isSubActive = actionSubTab === subTab;
                      return (
                        <button
                          key={subTab}
                          onClick={() => setActionSubTab(subTab as any)}
                          className={`px-4 py-2 text-[11px] font-bold rounded-xl whitespace-nowrap transition cursor-pointer ${
                            isSubActive
                              ? "bg-white text-slate-900 border border-slate-200/50 shadow-sm"
                              : "bg-transparent text-slate-550 hover:text-slate-800 hover:bg-white/40"
                          }`}
                        >
                          {subTab}
                        </button>
                      );
                    })}
                  </div>

                  {/* Sub-tab Content Cards */}
                  <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
                    
                    {/* APPRECIATIONS */}
                    {actionSubTab === "Appreciations" && (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h3 className="text-sm font-bold text-[#212121] flex items-center gap-2">
                            <IconAward size={18} className="text-slate-600" />
                            Employee Appreciation
                          </h3>
                          <button
                            onClick={() => setShowAddAppreciationModal(true)}
                            className="px-4 py-2 bg-[#212121] hover:bg-black text-white text-[11px] font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5"
                          >
                            <IconPlus size={14} /> Add Appreciation
                          </button>
                        </div>

                        {appreciations.length === 0 ? (
                          <div className="py-16 text-center flex flex-col justify-center items-center">
                            <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-3">
                              <IconFileText size={28} className="text-slate-300" />
                            </div>
                            <h3 className="text-xs font-bold text-slate-700">No appreciation found</h3>
                            <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                              We couldn't find any data to show here.
                            </p>
                          </div>
                        ) : (
                          <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-xs">
                            <table className="w-full text-left border-collapse text-xs">
                              <thead>
                                <tr className="bg-slate-50 border-b border-slate-100 text-gray-500 font-bold">
                                  <th className="px-4 py-3">SR.NO</th>
                                  <th className="px-4 py-3">AWARD TYPE</th>
                                  <th className="px-4 py-3">RECOGNIZED BY</th>
                                  <th className="px-4 py-3">GIVEN ON</th>
                                  <th className="px-4 py-3">VISIBILITY</th>
                                  <th className="px-4 py-3">SUMMARY</th>
                                  <th className="px-4 py-3 text-center">ACTIONS</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50 font-semibold text-slate-700">
                                {appreciations.map((appr, idx) => (
                                  <tr key={appr.id} className="hover:bg-slate-50/20 transition-colors">
                                    <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                                    <td className="px-4 py-3 text-[#212121]">{appr.award}</td>
                                    <td className="px-4 py-3 text-slate-800">{appr.recognizedBy}</td>
                                    <td className="px-4 py-3 text-gray-550">{appr.givenOn}</td>
                                    <td className="px-4 py-3">
                                      <span className={`px-2 py-0.5 rounded-full font-bold text-[9px] ${
                                        appr.visibility === "Public" ? "bg-green-50 text-green-600" : "bg-slate-100 text-slate-600"
                                      }`}>
                                        {appr.visibility}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-450 max-w-[150px] truncate">{appr.summary || "-"}</td>
                                    <td className="px-4 py-3 text-center">
                                      <button 
                                        onClick={() => {
                                          setAppreciations(prev => prev.filter(item => item.id !== appr.id));
                                          toast.success("Appreciation removed!");
                                        }}
                                        className="text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* APPRAISAL */}
                    {actionSubTab === "Appraisal" && (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h3 className="text-sm font-bold text-[#212121] flex items-center gap-2">
                            <IconTrendingUp size={18} className="text-slate-600" />
                            Employee Appraisal
                          </h3>
                          <button
                            onClick={() => setShowAddAppraisalModal(true)}
                            className="px-4 py-2 bg-[#212121] hover:bg-black text-white text-[11px] font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5"
                          >
                            <IconPlus size={14} /> Add Appraisal
                          </button>
                        </div>

                        {appraisals.length === 0 ? (
                          <div className="py-16 text-center flex flex-col justify-center items-center">
                            <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-3">
                              <IconFileText size={28} className="text-slate-300" />
                            </div>
                            <h3 className="text-xs font-bold text-slate-700">No appraisal records found</h3>
                            <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                              We couldn't find any data to show here.
                            </p>
                          </div>
                        ) : (
                          <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-xs">
                            <table className="w-full text-left border-collapse text-xs">
                              <thead>
                                <tr className="bg-slate-50 border-b border-slate-100 text-gray-500 font-bold">
                                  <th className="px-4 py-3">SR.NO</th>
                                  <th className="px-4 py-3">NEW DESIGNATION</th>
                                  <th className="px-4 py-3">OLD CTC (₹)</th>
                                  <th className="px-4 py-3">NEW CTC (₹)</th>
                                  <th className="px-4 py-3">INCREMENT (₹)</th>
                                  <th className="px-4 py-3">INC. (%)</th>
                                  <th className="px-4 py-3">EFFECTIVE DATE</th>
                                  <th className="px-4 py-3 text-center">ACTIONS</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50 font-semibold text-slate-700">
                                {appraisals.map((apr, idx) => (
                                  <tr key={apr.id} className="hover:bg-slate-50/20 transition-colors">
                                    <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                                    <td className="px-4 py-3 text-[#212121]">{apr.newDesignation}</td>
                                    <td className="px-4 py-3 text-slate-800">₹ {apr.oldCtc}</td>
                                    <td className="px-4 py-3 text-slate-800 font-bold">₹ {apr.newCtc}</td>
                                    <td className="px-4 py-3 text-[#22C55E]">₹ {apr.incrementAmount}</td>
                                    <td className="px-4 py-3 text-[#22C55E]">+{apr.incrementPercentage}%</td>
                                    <td className="px-4 py-3 text-gray-550">{apr.effectiveDate}</td>
                                    <td className="px-4 py-3 text-center">
                                      <button 
                                        onClick={() => {
                                          setAppraisals(prev => prev.filter(item => item.id !== apr.id));
                                          toast.success("Appraisal record removed!");
                                        }}
                                        className="text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* RELEGATION */}
                    {actionSubTab === "Relegation" && (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h3 className="text-sm font-bold text-[#212121] flex items-center gap-2">
                            <IconTrendingDown size={18} className="text-slate-600" />
                            Employee Relegation
                          </h3>
                          <button
                            onClick={() => setShowAddRelegationModal(true)}
                            className="px-4 py-2 bg-[#212121] hover:bg-black text-white text-[11px] font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5"
                          >
                            <IconPlus size={14} /> Add Relegation
                          </button>
                        </div>

                        {relegations.length === 0 ? (
                          <div className="py-16 text-center flex flex-col justify-center items-center">
                            <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-3">
                              <IconFileText size={28} className="text-slate-300" />
                            </div>
                            <h3 className="text-xs font-bold text-slate-700">No relegation records found</h3>
                            <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                              We couldn't find any data to show here.
                            </p>
                          </div>
                        ) : (
                          <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-xs">
                            <table className="w-full text-left border-collapse text-xs">
                              <thead>
                                <tr className="bg-slate-50 border-b border-slate-100 text-gray-500 font-bold">
                                  <th className="px-4 py-3">SR.NO</th>
                                  <th className="px-4 py-3">NEW DESIGNATION</th>
                                  <th className="px-4 py-3">OLD CTC (₹)</th>
                                  <th className="px-4 py-3">NEW CTC (₹)</th>
                                  <th className="px-4 py-3">DECREMENT (₹)</th>
                                  <th className="px-4 py-3">DEC. (%)</th>
                                  <th className="px-4 py-3">EFFECTIVE DATE</th>
                                  <th className="px-4 py-3 text-center">ACTIONS</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50 font-semibold text-slate-700">
                                {relegations.map((rel, idx) => (
                                  <tr key={rel.id} className="hover:bg-slate-50/20 transition-colors">
                                    <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                                    <td className="px-4 py-3 text-[#212121]">{rel.newDesignation}</td>
                                    <td className="px-4 py-3 text-slate-800">₹ {rel.oldCtc}</td>
                                    <td className="px-4 py-3 text-slate-800 font-bold">₹ {rel.newCtc}</td>
                                    <td className="px-4 py-3 text-red-500">₹ {rel.decrementAmount}</td>
                                    <td className="px-4 py-3 text-red-500">-{rel.decrementPercentage}%</td>
                                    <td className="px-4 py-3 text-gray-550">{rel.effectiveDate}</td>
                                    <td className="px-4 py-3 text-center">
                                      <button 
                                        onClick={() => {
                                          setRelegations(prev => prev.filter(item => item.id !== rel.id));
                                          toast.success("Relegation record removed!");
                                        }}
                                        className="text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* FINES & WARNING */}
                    {actionSubTab === "Fines & Warning" && (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h3 className="text-sm font-bold text-[#212121] flex items-center gap-2">
                            <IconAlertTriangle size={18} className="text-slate-600" />
                            Employee Fines & Warnings
                          </h3>
                          <button
                            onClick={() => setShowAddFineWarningModal(true)}
                            className="px-4 py-2 bg-[#212121] hover:bg-black text-white text-[11px] font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5"
                          >
                            <IconPlus size={14} /> Add Fine/Warning
                          </button>
                        </div>

                        {finesWarnings.length === 0 ? (
                          <div className="py-16 text-center flex flex-col justify-center items-center">
                            <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-3">
                              <IconFileText size={28} className="text-slate-300" />
                            </div>
                            <h3 className="text-xs font-bold text-slate-700">No records found</h3>
                            <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                              We couldn't find any data to show here.
                            </p>
                          </div>
                        ) : (
                          <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-xs">
                            <table className="w-full text-left border-collapse text-xs">
                              <thead>
                                <tr className="bg-slate-50 border-b border-slate-100 text-gray-500 font-bold">
                                  <th className="px-4 py-3">SR.NO</th>
                                  <th className="px-4 py-3">TYPE</th>
                                  <th className="px-4 py-3">AMOUNT (₹)</th>
                                  <th className="px-4 py-3">DATE</th>
                                  <th className="px-4 py-3">REASON</th>
                                  <th className="px-4 py-3 text-center">ACTIONS</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50 font-semibold text-slate-700">
                                {finesWarnings.map((fw, idx) => (
                                  <tr key={fw.id} className="hover:bg-slate-50/20 transition-colors">
                                    <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                                    <td className="px-4 py-3">
                                      <span className={`px-2 py-0.5 rounded-full font-extrabold text-[10px] ${
                                        fw.type === "FINE" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"
                                      }`}>
                                        {fw.type}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-[#212121]">{fw.amount}</td>
                                    <td className="px-4 py-3 text-gray-550">{fw.date}</td>
                                    <td className="px-4 py-3 text-gray-400 max-w-[150px] truncate">{fw.reason}</td>
                                    <td className="px-4 py-3 text-center">
                                      <button 
                                        onClick={() => {
                                          setFinesWarnings(prev => prev.filter(item => item.id !== fw.id));
                                          toast.success("Record removed!");
                                        }}
                                        className="text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* REWARDS */}
                    {actionSubTab === "Rewards" && (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h3 className="text-sm font-bold text-[#212121] flex items-center gap-2">
                            <IconGift size={18} className="text-slate-600" />
                            Employee Rewards
                          </h3>
                          <button
                            onClick={() => setShowAddRewardModal(true)}
                            className="px-4 py-2 bg-[#212121] hover:bg-black text-white text-[11px] font-bold rounded-xl transition cursor-pointer flex items-center gap-1.5"
                          >
                            <IconPlus size={14} /> Add Reward
                          </button>
                        </div>

                        {rewards.length === 0 ? (
                          <div className="py-16 text-center flex flex-col justify-center items-center">
                            <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-3">
                              <IconFileText size={28} className="text-slate-300" />
                            </div>
                            <h3 className="text-xs font-bold text-slate-700">No rewards found</h3>
                            <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                              We couldn't find any data to show here.
                            </p>
                          </div>
                        ) : (
                          <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-xs">
                            <table className="w-full text-left border-collapse text-xs">
                              <thead>
                                <tr className="bg-slate-50 border-b border-slate-100 text-gray-500 font-bold">
                                  <th className="px-4 py-3">SR.NO</th>
                                  <th className="px-4 py-3">REWARD TYPE</th>
                                  <th className="px-4 py-3">AMOUNT (₹)</th>
                                  <th className="px-4 py-3">GIVEN ON</th>
                                  <th className="px-4 py-3">DESCRIPTION</th>
                                  <th className="px-4 py-3 text-center">ACTIONS</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50 font-semibold text-slate-700">
                                {rewards.map((rw, idx) => (
                                  <tr key={rw.id} className="hover:bg-slate-50/20 transition-colors">
                                    <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                                    <td className="px-4 py-3 text-[#212121]">{rw.name}</td>
                                    <td className="px-4 py-3 text-slate-800">{rw.amount === "-" ? "-" : `₹ ${rw.amount}`}</td>
                                    <td className="px-4 py-3 text-gray-555">{rw.date}</td>
                                    <td className="px-4 py-3 text-gray-400 max-w-[150px] truncate">{rw.reason}</td>
                                    <td className="px-4 py-3 text-center">
                                      <button 
                                        onClick={() => {
                                          setRewards(prev => prev.filter(item => item.id !== rw.id));
                                          toast.success("Reward removed!");
                                        }}
                                        className="text-red-500 hover:text-red-700 bg-transparent border-none cursor-pointer"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* RESIGN & TERMINATE */}
                    {actionSubTab === "Resign & Terminate" && (
                      <div className="space-y-6">
                        <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                          <h3 className="text-sm font-bold text-[#212121] flex items-center gap-2">
                            <IconRotate size={18} className="text-slate-600 animate-spin" style={{ animationDuration: "10s" }} />
                            Resign & Terminate Status
                          </h3>
                        </div>

                        {!resignTerminateInfo ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            
                            {/* Resign Option Card */}
                            <div className="border border-slate-200 hover:border-[#FBD037] rounded-3xl p-6 space-y-4 bg-slate-50/20 hover:shadow-md transition duration-200">
                              <h4 className="font-bold text-[#212121] text-xs uppercase tracking-wider flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-amber-500" />
                                Initiate Resignation
                              </h4>
                              <p className="text-[10.5px] text-gray-400 leading-relaxed font-semibold">
                                Officially process employee resignation. Enter resignation date, final working day, and reason parameters.
                              </p>
                              <button
                                onClick={() => {
                                  setResignTerminateActionType("RESIGN");
                                  setShowResignTerminateModal(true);
                                }}
                                className="px-4 py-2 border border-amber-300 text-amber-600 bg-amber-50/30 hover:bg-amber-50 text-[11px] font-bold rounded-xl transition cursor-pointer"
                              >
                                Process Resignation
                              </button>
                            </div>

                            {/* Terminate Option Card */}
                            <div className="border border-slate-200 hover:border-red-400 rounded-3xl p-6 space-y-4 bg-slate-50/20 hover:shadow-md transition duration-200">
                              <h4 className="font-bold text-[#212121] text-xs uppercase tracking-wider flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-red-500" />
                                Terminate Employee
                              </h4>
                              <p className="text-[10.5px] text-gray-400 leading-relaxed font-semibold">
                                Terminate the employment contract of this employee immediately. This updates their status globally to inactive.
                              </p>
                              <button
                                onClick={() => {
                                  setResignTerminateActionType("TERMINATE");
                                  setShowResignTerminateModal(true);
                                }}
                                className="px-4 py-2 border border-red-200 text-red-500 bg-red-50/30 hover:bg-red-50 text-[11px] font-bold rounded-xl transition cursor-pointer"
                              >
                                Terminate Employee
                              </button>
                            </div>

                          </div>
                        ) : (
                          <div className="bg-slate-50/50 border border-slate-200/80 rounded-3xl p-6 space-y-4">
                            <div className="flex items-center gap-3">
                              <span className={`px-2.5 py-0.5 rounded-full font-extrabold text-[10px] uppercase ${
                                resignTerminateInfo.type === "RESIGN" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                              }`}>
                                {resignTerminateInfo.type === "RESIGN" ? "Resigned" : "Terminated"}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-xs font-semibold text-slate-700">
                              <div>
                                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Date Actioned</div>
                                <div className="mt-0.5">{resignTerminateInfo.date}</div>
                              </div>
                              <div>
                                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Final Working Day</div>
                                <div className="mt-0.5">{resignTerminateInfo.finalDay || "-"}</div>
                              </div>
                              <div className="col-span-2">
                                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Reason</div>
                                <div className="mt-0.5 text-slate-600 font-medium leading-relaxed">{resignTerminateInfo.reason || "-"}</div>
                              </div>
                            </div>

                            <div className="pt-3 border-t border-dashed border-slate-200 flex justify-end">
                              <button
                                onClick={() => {
                                  setResignTerminateInfo(null);
                                  toast.success("Action reset successfully!");
                                }}
                                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-[11px] font-bold rounded-xl transition cursor-pointer"
                              >
                                Revert Status to Active
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                </div>
              )}

              {/* ── OTHER TABS (generic placeholder) ──────── */}
              {TABS.slice(3).filter(t => t.label !== "Leaves" && t.label !== "Attendance" && t.label !== "Action").map((tab) => (
                activeTab === tab.label && (
                  <div key={tab.label} className="py-12 text-center space-y-2">
                    <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center mx-auto text-slate-400">
                      {tab.icon}
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider">{tab.label}</h3>
                      <p className="text-[9px] text-slate-400 mt-1">No custom records found.</p>
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        </div>
      )}
      {/* ── ADD NEW LEAVE MODAL ─────────────────────── */}
      {showAddLeaveModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !isLeaveSubmitting && setShowAddLeaveModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl z-10 my-4 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">Add New Leave</h2>
              <button
                onClick={() => !isLeaveSubmitting && setShowAddLeaveModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 cursor-pointer"
              >
                <IconX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Day Type Radio */}
              <div className="flex items-center gap-4">
                {(["FULL_DAY", "MULTIPLE", "FIRST_HALF", "SECOND_HALF"] as const).map((dt) => (
                  <label key={dt} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="dayType"
                      checked={leaveFormType === dt}
                      onChange={() => setLeaveFormType(dt)}
                      className="accent-[#212121] w-3.5 h-3.5"
                    />
                    <span className="text-xs font-semibold text-slate-700">
                      {dt === "FULL_DAY" ? "Full Day" : dt === "MULTIPLE" ? "Multiple" : dt === "FIRST_HALF" ? "First Half" : "Second Half"}
                    </span>
                  </label>
                ))}
              </div>

              {/* Date + Employee */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Select Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={leaveFormDate}
                    onChange={(e) => setLeaveFormDate(e.target.value)}
                    className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Employee <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={emp.name || ""}
                    readOnly
                    className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-slate-50 border border-slate-200 outline-none cursor-not-allowed"
                  />
                </div>
              </div>

              {/* End Date (only for Multiple) */}
              {leaveFormType === "MULTIPLE" && (
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">End Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={leaveFormEndDate}
                    onChange={(e) => setLeaveFormEndDate(e.target.value)}
                    className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all"
                  />
                </div>
              )}

              {/* Leave Type + Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Leave Type <span className="text-red-500">*</span></label>
                  <div className="flex items-center gap-2">
                    <select
                      value={leaveFormLeaveTypeId}
                      onChange={(e) => setLeaveFormLeaveTypeId(e.target.value)}
                      className="flex-1 rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all cursor-pointer"
                    >
                      <option value="">Select Leave Type...</option>
                      {leaveTypes.map((lt: any) => (
                        <option key={lt.id || lt._id} value={lt.id || lt._id}>{lt.leaveType || lt.name || lt.leaveTypeName}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        setNewLeaveTypeName("");
                        setNewLeaveTypeCount("1");
                        setNewLeaveTypeMonthlyLimit("1");
                        setNewLeaveTypeColor("#FF0000");
                        setNewLeaveTypePaid(false);
                        setShowNewLeaveTypeModal(true);
                      }}
                      className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl bg-[#212121] hover:bg-[#333] text-white transition cursor-pointer"
                      title="Create new leave type"
                    >
                      <IconPlus size={16} />
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Status</label>
                  <select
                    value={leaveFormStatus}
                    onChange={(e) => setLeaveFormStatus(e.target.value)}
                    className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all cursor-pointer"
                  >
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Reason */}
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Reason For Absence <span className="text-red-500">*</span></label>
                <textarea
                  value={leaveFormReason}
                  onChange={(e) => setLeaveFormReason(e.target.value)}
                  rows={3}
                  placeholder="Enter Reason For Absence"
                  className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all resize-none"
                />
              </div>

              {/* Document Upload */}
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-emerald-600 uppercase tracking-wider">Documents</label>
                <label className="flex flex-col items-center justify-center py-6 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#FBD037] hover:bg-[#FBD037]/5 transition-all">
                  <IconUpload size={24} className="text-slate-400 mb-2" />
                  <span className="text-xs font-bold text-slate-700">
                    {leaveFormDocument ? leaveFormDocument.name : "Drag & drop or click to browse"}
                  </span>
                  <span className="text-[10px] text-blue-400 mt-1">Maximum 10 files • Max 3MB per file</span>
                  <span className="text-[9px] text-slate-400">Accepted: image/*, application/pdf</span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => setLeaveFormDocument(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
              <button
                onClick={() => setShowAddLeaveModal(false)}
                disabled={isLeaveSubmitting}
                className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitLeave}
                disabled={isLeaveSubmitting}
                className="px-6 py-2 bg-[#212121] text-white rounded-xl text-xs font-bold hover:bg-[#333] transition cursor-pointer disabled:opacity-60 flex items-center gap-2"
              >
                {isLeaveSubmitting && (
                  <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── NEW LEAVE TYPE MODAL ─────────────────────────────── */}
      {showNewLeaveTypeModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !isCreatingLeaveType && setShowNewLeaveTypeModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl z-10 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">New Leave Type</h2>
              <button
                onClick={() => !isCreatingLeaveType && setShowNewLeaveTypeModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 cursor-pointer"
              >
                <IconX size={16} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1.5 px-6 pt-4">
              {(["General", "Entitlement", "Applicability"] as const).map((tab) => {
                const isActive = newLeaveTypeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setNewLeaveTypeTab(tab)}
                    className={`px-4 py-1.5 text-[11px] font-bold rounded-lg transition-all ${
                      isActive
                        ? "bg-[#FBD037] text-[#131327] shadow-xs"
                        : "text-slate-500 hover:bg-slate-100 cursor-pointer"
                    }`}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* ── GENERAL TAB ── */}
              {newLeaveTypeTab === "General" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Leave Type <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={newLeaveTypeName}
                        onChange={(e) => setNewLeaveTypeName(e.target.value)}
                        placeholder="Enter leave type (Ex. Casual, Sick)"
                        className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">No of Leaves <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        min="0"
                        value={newLeaveTypeCount}
                        onChange={(e) => setNewLeaveTypeCount(e.target.value)}
                        className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Monthly Limit <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        min="0"
                        value={newLeaveTypeMonthlyLimit}
                        onChange={(e) => setNewLeaveTypeMonthlyLimit(e.target.value)}
                        className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={newLeaveTypeColor}
                          onChange={(e) => setNewLeaveTypeColor(e.target.value)}
                          className="w-10 h-9 rounded-xl border border-slate-200 cursor-pointer bg-white p-0.5"
                        />
                        <div
                          className="flex-1 h-9 rounded-xl border border-slate-200 transition-all"
                          style={{ backgroundColor: newLeaveTypeColor }}
                        />
                      </div>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer pt-2">
                    <input
                      type="checkbox"
                      checked={newLeaveTypePaid}
                      onChange={(e) => setNewLeaveTypePaid(e.target.checked)}
                      className="accent-[#212121] w-4 h-4 rounded border-slate-200"
                    />
                    <span className="text-xs font-semibold text-slate-700">Leave Paid</span>
                  </label>
                </div>
              )}

              {/* ── ENTITLEMENT TAB ── */}
              {newLeaveTypeTab === "Entitlement" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Effective After</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          value={newLeaveTypeEffectiveAfter}
                          onChange={(e) => setNewLeaveTypeEffectiveAfter(e.target.value)}
                          className="w-24 rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all"
                        />
                        <span className="text-xs font-semibold text-slate-500">of joining</span>
                        <select
                          value={newLeaveTypeEffectiveUnit}
                          onChange={(e) => setNewLeaveTypeEffectiveUnit(e.target.value as any)}
                          className="flex-1 rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all cursor-pointer"
                        >
                          <option value="DAYS">Days</option>
                          <option value="MONTHS">Months</option>
                          <option value="YEARS">Years</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Unused Leaves <span className="text-red-500">*</span></label>
                      <select
                        value={newLeaveTypeUnused}
                        onChange={(e) => setNewLeaveTypeUnused(e.target.value as any)}
                        className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all cursor-pointer"
                      >
                        <option value="CARRY_FORWARD">Carry Forward</option>
                        <option value="ENCASHMENT">Encashment</option>
                        <option value="EXPIRE">Lapsed</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newLeaveTypeInNotice}
                        onChange={(e) => setNewLeaveTypeInNotice(e.target.checked)}
                        className="accent-[#212121] w-4 h-4 rounded border-slate-200"
                      />
                      <span className="text-xs font-semibold text-slate-700">In Notice Period</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newLeaveTypeInProbation}
                        onChange={(e) => setNewLeaveTypeInProbation(e.target.checked)}
                        className="accent-[#212121] w-4 h-4 rounded border-slate-200"
                      />
                      <span className="text-xs font-semibold text-slate-700">In Probation</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newLeaveTypePriorPermission}
                        onChange={(e) => setNewLeaveTypePriorPermission(e.target.checked)}
                        className="accent-[#212121] w-4 h-4 rounded border-slate-200"
                      />
                      <span className="text-xs font-semibold text-slate-700">Prior Permission</span>
                    </label>
                  </div>
                </div>
              )}

              {/* ── APPLICABILITY TAB ── */}
              {newLeaveTypeTab === "Applicability" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Departments dropdown */}
                    <div className={`space-y-1 relative ${isDepDropdownOpen ? "z-20" : "z-10"}`}>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Departments</label>
                      <button
                        type="button"
                        onClick={() => {
                          setIsDepDropdownOpen(!isDepDropdownOpen);
                          setIsDesDropdownOpen(false);
                          setIsRoleDropdownOpen(false);
                        }}
                        className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none text-left flex justify-between items-center hover:bg-slate-50"
                      >
                        <span className="truncate">
                          {newLeaveTypeDepartments.length > 0
                            ? `${newLeaveTypeDepartments.length} Selected`
                            : "Select Option..."}
                        </span>
                        <span className="text-slate-400">▼</span>
                      </button>
                      {isDepDropdownOpen && (
                        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto p-2 space-y-1">
                          {departmentsList.map((dep: any) => {
                            const depId = dep.id || dep._id;
                            const isChecked = newLeaveTypeDepartments.includes(depId);
                            return (
                              <label key={depId} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded-lg cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    if (isChecked) {
                                      setNewLeaveTypeDepartments(newLeaveTypeDepartments.filter((id) => id !== depId));
                                    } else {
                                      setNewLeaveTypeDepartments([...newLeaveTypeDepartments, depId]);
                                    }
                                  }}
                                  className="accent-[#212121] w-3.5 h-3.5"
                                />
                                <span className="text-xs font-semibold text-slate-700">{dep.name || dep.departmentName}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Designations dropdown */}
                    <div className={`space-y-1 relative ${isDesDropdownOpen ? "z-20" : "z-10"}`}>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Designations</label>
                      <button
                        type="button"
                        onClick={() => {
                          setIsDesDropdownOpen(!isDesDropdownOpen);
                          setIsDepDropdownOpen(false);
                          setIsRoleDropdownOpen(false);
                        }}
                        className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none text-left flex justify-between items-center hover:bg-slate-50"
                      >
                        <span className="truncate">
                          {newLeaveTypeDesignations.length > 0
                            ? `${newLeaveTypeDesignations.length} Selected`
                            : "Select Option..."}
                        </span>
                        <span className="text-slate-400">▼</span>
                      </button>
                      {isDesDropdownOpen && (
                        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto p-2 space-y-1">
                          {designationsList.map((des: any) => {
                            const desId = des.id || des._id;
                            const isChecked = newLeaveTypeDesignations.includes(desId);
                            return (
                              <label key={desId} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded-lg cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    if (isChecked) {
                                      setNewLeaveTypeDesignations(newLeaveTypeDesignations.filter((id) => id !== desId));
                                    } else {
                                      setNewLeaveTypeDesignations([...newLeaveTypeDesignations, desId]);
                                    }
                                  }}
                                  className="accent-[#212121] w-3.5 h-3.5"
                                />
                                <span className="text-xs font-semibold text-slate-700">{des.name || des.designationName}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* User Roles dropdown */}
                    <div className={`space-y-1 relative ${isRoleDropdownOpen ? "z-20" : "z-10"}`}>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">User Roles</label>
                      <button
                        type="button"
                        onClick={() => {
                          setIsRoleDropdownOpen(!isRoleDropdownOpen);
                          setIsDepDropdownOpen(false);
                          setIsDesDropdownOpen(false);
                        }}
                        className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none text-left flex justify-between items-center hover:bg-slate-50"
                      >
                        <span className="truncate">
                          {newLeaveTypeRoles.length > 0
                            ? `${newLeaveTypeRoles.length} Selected`
                            : "Select User Roles..."}
                        </span>
                        <span className="text-slate-400">▼</span>
                      </button>
                      {isRoleDropdownOpen && (
                        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto p-2 space-y-1">
                          {rolesList.map((role: any) => {
                            const roleId = role.id || role._id;
                            const isChecked = newLeaveTypeRoles.includes(roleId);
                            return (
                              <label key={roleId} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded-lg cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {
                                    if (isChecked) {
                                      setNewLeaveTypeRoles(newLeaveTypeRoles.filter((id) => id !== roleId));
                                    } else {
                                      setNewLeaveTypeRoles([...newLeaveTypeRoles, roleId]);
                                    }
                                  }}
                                  className="accent-[#212121] w-3.5 h-3.5"
                                />
                                <span className="text-xs font-semibold text-slate-700">{role.roleName || role.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Gender select */}
                    <div className="space-y-1">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Gender</label>
                      <select
                        value={newLeaveTypeGender}
                        onChange={(e) => setNewLeaveTypeGender(e.target.value as any)}
                        className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all cursor-pointer"
                      >
                        <option value="ALL">Select Gender (All)</option>
                        <option value="MALE">Male</option>
                        <option value="FEMALE">Female</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Marital Status select */}
                    <div className="space-y-1">
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">Marital Status</label>
                      <select
                        value={newLeaveTypeMaritalStatus}
                        onChange={(e) => setNewLeaveTypeMaritalStatus(e.target.value as any)}
                        className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all cursor-pointer"
                      >
                        <option value="ALL">Select Marital Status (All)</option>
                        <option value="SINGLE">Single</option>
                        <option value="MARRIED">Married</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-slate-100">
              <button
                disabled={isCreatingLeaveType}
                onClick={async () => {
                  if (!newLeaveTypeName.trim()) { toast.error("Leave type name is required"); return; }
                  if (!newLeaveTypeCount || Number(newLeaveTypeCount) < 0) { toast.error("No of leaves is required"); return; }
                  if (!newLeaveTypeMonthlyLimit || Number(newLeaveTypeMonthlyLimit) < 0) { toast.error("Monthly limit is required"); return; }

                  setIsCreatingLeaveType(true);
                  try {
                    const payload: any = {
                      leaveType: newLeaveTypeName.trim(),
                      noOfLeaves: Number(newLeaveTypeCount),
                      monthlyLimit: Number(newLeaveTypeMonthlyLimit),
                      color: newLeaveTypeColor,
                      isPaidLeave: newLeaveTypePaid,
                      effectiveAfter: Number(newLeaveTypeEffectiveAfter || 0),
                      effectiveAfterUnit: newLeaveTypeEffectiveUnit,
                      unusedLeavesPolicy: newLeaveTypeUnused,
                      allowInNoticePeriod: newLeaveTypeInNotice,
                      allowInProbation: newLeaveTypeInProbation,
                      priorPermissionRequired: newLeaveTypePriorPermission,
                      departmentIds: newLeaveTypeDepartments,
                      designationIds: newLeaveTypeDesignations,
                      userRoleIds: newLeaveTypeRoles,
                      genderFilters: newLeaveTypeGender === "ALL" ? [] : [newLeaveTypeGender],
                      maritalStatusFilters: newLeaveTypeMaritalStatus === "ALL" ? [] : [newLeaveTypeMaritalStatus]
                    };
                    
                    if (raw.companyId) payload.companyId = raw.companyId;
                    if (raw.branchId) payload.branchId = raw.branchId;

                    const res = await createHrLeaveType(payload);
                    if (res.success) {
                      toast.success("Leave type created successfully!");
                      setShowNewLeaveTypeModal(false);
                      // Refresh leave types list
                      const typeRes = await fetchHrLeaveTypes(1, 100);
                      if (typeRes.success && typeRes.data) {
                        let types: any[] = [];
                        if (Array.isArray(typeRes.data)) types = typeRes.data;
                        else if (typeRes.data.leaveTypes) types = typeRes.data.leaveTypes;
                        else {
                          const found = Object.values(typeRes.data).find(Array.isArray);
                          if (found) types = found as any[];
                        }
                        setLeaveTypes(types);
                        // Auto-select the newly created type
                        const newType = types.find((t: any) => (t.name || t.leaveTypeName) === newLeaveTypeName.trim());
                        if (newType) setLeaveFormLeaveTypeId(newType.id || newType._id);
                      }
                    } else {
                      toast.error(res.message || "Failed to create leave type");
                    }
                  } catch (err: any) {
                    toast.error(err?.message || "Failed to create leave type");
                  } finally {
                    setIsCreatingLeaveType(false);
                  }
                }}
                className="px-6 py-2 bg-[#212121] text-white rounded-xl text-xs font-bold hover:bg-[#333] transition cursor-pointer disabled:opacity-60 flex items-center gap-2"
              >
                {isCreatingLeaveType && (
                  <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                Create Leave Type
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── UPLOAD DOCUMENT MODAL ─────────────────────── */}
      {showUploadDocModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 animate-in fade-in duration-200" onClick={() => !isDocUploading && setShowUploadDocModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg z-10 my-4 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h2 className="text-base font-bold text-slate-900">
                  {docModalMode === "ADD" ? "Upload Document" : "Update Document"}
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {docModalMode === "ADD" ? "Select document type and upload your file here." : "Modify document details below."}
                </p>
              </div>
              <button
                onClick={() => !isDocUploading && setShowUploadDocModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 cursor-pointer"
              >
                <IconX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Document Type select */}
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Document Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={uploadDocType}
                  onChange={(e) => setUploadDocType(e.target.value)}
                  className="w-full rounded-xl py-2.5 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all cursor-pointer"
                >
                  <option value="">Select document type...</option>
                  <option value="Aadhar Card">Aadhar Card</option>
                  <option value="PAN Card">PAN Card</option>
                  <option value="Work Contract">Work Contract</option>
                  <option value="Mark Sheet">Mark Sheet</option>
                  <option value="Experience Letter">Experience Letter</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* File Upload Zone */}
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  {docModalMode === "ADD" ? "Upload File *" : "Replace File (Optional)"}
                </label>
                <label className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#FBD037] hover:bg-[#FBD037]/5 transition-all">
                  <IconUpload size={28} className="text-slate-400 mb-2" />
                  <span className="text-xs font-bold text-slate-700">
                    {uploadDocFile ? uploadDocFile.name : "Click to upload"}
                  </span>
                  <span className="text-[10px] text-slate-400 mt-1">Max 10MB</span>
                  <span className="text-[9px] text-slate-400">Accepted: image/*, application/pdf</span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => setUploadDocFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/20">
              <button
                onClick={() => setShowUploadDocModal(false)}
                disabled={isDocUploading}
                className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!uploadDocType) {
                    toast.error("Please select a document type");
                    return;
                  }

                  if (docModalMode === "ADD" && !uploadDocFile) {
                    toast.error("Please select a file to upload");
                    return;
                  }

                  setIsDocUploading(true);
                  const loader = toast.loading(docModalMode === "ADD" ? "Uploading document..." : "Updating document...");

                  try {
                    let res;
                    if (docModalMode === "ADD") {
                      const existingDocs = Array.isArray(raw.documents) ? raw.documents.map((d: any) => ({
                        name: d.name || d.documentName || "",
                        type: d.type || d.documentType || "Other",
                        url: d.url || d.attachmentUrl || ""
                      })) : [];

                      const updatedDocs = [
                        ...existingDocs,
                        { name: uploadDocFile!.name, type: uploadDocType, url: "" }
                      ];

                      const formData = new FormData();
                      formData.append("documents", JSON.stringify(updatedDocs));
                      formData.append("docs", uploadDocFile!);

                      res = await updateHrEmployee(id!, formData);
                    } else {
                      // Mode: EDIT
                      if (selectedDocForEdit?.id?.startsWith("mock-")) {
                        // Mock update fallback
                        setEmployeeDocuments(prev => prev.map(d => d.id === selectedDocForEdit.id ? {
                          ...d,
                          type: uploadDocType,
                          name: uploadDocFile ? uploadDocFile.name : d.name,
                          url: uploadDocFile ? URL.createObjectURL(uploadDocFile) : d.url
                        } : d));
                        toast.success("Mock document updated successfully!", { id: loader });
                        setIsDocUploading(false);
                        setShowUploadDocModal(false);
                        return;
                      }

                      const existingDocs = Array.isArray(raw.documents) ? raw.documents.map((d: any) => ({
                        name: d.name || d.documentName || "",
                        type: d.type || d.documentType || "Other",
                        url: d.url || d.attachmentUrl || ""
                      })) : [];

                      const updatedDocs = existingDocs.map((d: any) => {
                        if (d.name === selectedDocForEdit.name) {
                          return {
                            ...d,
                            type: uploadDocType,
                            name: uploadDocFile ? uploadDocFile.name : d.name
                          };
                        }
                        return d;
                      });

                      const formData = new FormData();
                      formData.append("documents", JSON.stringify(updatedDocs));
                      if (uploadDocFile) {
                        formData.append("docs", uploadDocFile);
                      }

                      res = await updateHrEmployee(id!, formData);
                    }

                    if (res && res.success) {
                      toast.success(docModalMode === "ADD" ? "Document uploaded successfully!" : "Document updated successfully!", { id: loader });
                      loadEmployeeDetails();
                      setShowUploadDocModal(false);
                    } else {
                      toast.error(res?.message || "Failed to save document", { id: loader });
                    }
                  } catch (err: any) {
                    toast.error(err.message || "Failed to save document", { id: loader });
                  } finally {
                    setIsDocUploading(false);
                  }
                }}
                disabled={isDocUploading}
                className="px-6 py-2 bg-[#212121] text-white rounded-xl text-xs font-bold hover:bg-[#333] transition cursor-pointer disabled:opacity-60 flex items-center gap-2"
              >
                {isDocUploading && (
                  <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                {docModalMode === "ADD" ? "Upload" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── VIEW LEAVE DETAILS MODAL ─────────────────── */}
      {showViewLeaveModal && selectedLeaveForView && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowViewLeaveModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg z-10 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">Leave Details</h2>
              <button
                onClick={() => setShowViewLeaveModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 cursor-pointer bg-transparent border-none outline-none"
              >
                <IconX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Employee card */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-100 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center font-bold text-slate-600 border border-slate-150">
                  {emp.name ? emp.name[0].toUpperCase() : "E"}
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-bold text-slate-800">{emp.name || "-"}</h4>
                  <p className="text-[10px] text-slate-400 font-semibold">{raw.designationName || "Employee"}</p>
                </div>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase border ${
                  selectedLeaveForView.status === "APPROVED" ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                    : selectedLeaveForView.status === "REJECTED" ? "bg-red-50 text-red-500 border-red-200"
                    : "bg-amber-50 text-amber-600 border-amber-200"
                }`}>
                  {selectedLeaveForView.status || "PENDING"}
                </span>
              </div>

              {/* Basic Info section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
                  <h3 className="text-xs font-bold text-slate-700">Basic Info</h3>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-md text-[9px] font-bold uppercase tracking-wider">
                    {selectedLeaveForView.dayType === "FULL_DAY" ? "Full Day" 
                      : selectedLeaveForView.dayType === "MULTIPLE" ? "Multiple" 
                      : selectedLeaveForView.dayType === "FIRST_HALF" ? "First Half" : "Second Half"}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Type</label>
                    <span className="font-semibold text-slate-700">
                      {leaveTypes.find((t: any) => (t.id || t._id) === selectedLeaveForView.leaveTypeId)?.leaveType || leaveTypes.find((t: any) => (t.id || t._id) === selectedLeaveForView.leaveTypeId)?.name || selectedLeaveForView.leaveTypeName || "-"}
                    </span>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Applied Date</label>
                    <span className="font-semibold text-slate-700">
                      {selectedLeaveForView.createdAt ? new Date(selectedLeaveForView.createdAt).toLocaleDateString() : "-"}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Leave Period</label>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-700">
                        {selectedLeaveForView.leaveDate ? new Date(selectedLeaveForView.leaveDate).toLocaleDateString() : "-"}
                        {selectedLeaveForView.leaveDateEnd ? ` - ${new Date(selectedLeaveForView.leaveDateEnd).toLocaleDateString()}` : ""}
                      </span>
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[9px] font-bold uppercase">
                        {selectedLeaveForView.dayType === "MULTIPLE" ? "Multiple Days" : "1 Day"}
                      </span>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Absence Reason</label>
                    <p className="text-slate-600 bg-slate-50/50 p-2.5 rounded-lg border border-slate-100/55 whitespace-pre-wrap">
                      {selectedLeaveForView.reason || "-"}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Files</label>
                    <span className="font-medium text-slate-400">No files</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT LEAVE MODAL ───────────────────────── */}
      {showEditLeaveModal && selectedLeaveForEdit && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !isEditLeaveSubmitting && setShowEditLeaveModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl z-10 my-4 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">Edit Leave Request</h2>
              <button
                onClick={() => !isEditLeaveSubmitting && setShowEditLeaveModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 cursor-pointer bg-transparent border-none outline-none"
              >
                <IconX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Day Type Radio */}
              <div className="flex items-center gap-4">
                {(["FULL_DAY", "MULTIPLE", "FIRST_HALF", "SECOND_HALF"] as const).map((dt) => (
                  <label key={dt} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="editDayType"
                      checked={leaveFormType === dt}
                      onChange={() => setLeaveFormType(dt)}
                      className="accent-[#212121] w-3.5 h-3.5"
                    />
                    <span className="text-xs font-semibold text-slate-700">
                      {dt === "FULL_DAY" ? "Full Day" : dt === "MULTIPLE" ? "Multiple" : dt === "FIRST_HALF" ? "First Half" : "Second Half"}
                    </span>
                  </label>
                ))}
              </div>

              {/* Date Inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    {leaveFormType === "MULTIPLE" ? "Start Date" : "Date"} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={leaveFormDate}
                    onChange={(e) => setLeaveFormDate(e.target.value)}
                    className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all"
                  />
                </div>
                {leaveFormType === "MULTIPLE" && (
                  <div className="space-y-1">
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={leaveFormEndDate}
                      onChange={(e) => setLeaveFormEndDate(e.target.value)}
                      className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all"
                    />
                  </div>
                )}
              </div>

              {/* Leave Type Select */}
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Leave Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={leaveFormLeaveTypeId}
                  onChange={(e) => setLeaveFormLeaveTypeId(e.target.value)}
                  className="w-full rounded-xl py-2.5 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all cursor-pointer"
                >
                  <option value="">Select type...</option>
                  {leaveTypes.map((t: any) => (
                    <option key={t.id || t._id} value={t.id || t._id}>{t.leaveType || t.name || t.leaveTypeName}</option>
                  ))}
                </select>
              </div>

              {/* Status Select */}
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={leaveFormStatus}
                  onChange={(e) => setLeaveFormStatus(e.target.value as any)}
                  className="w-full rounded-xl py-2.5 px-3 text-xs font-semibold text-slate-800 bg-white border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all cursor-pointer"
                >
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              {/* Reason Textarea */}
              <div className="space-y-1">
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={leaveFormReason}
                  onChange={(e) => setLeaveFormReason(e.target.value)}
                  rows={3}
                  className="w-full rounded-xl py-2 px-3 text-xs font-semibold text-slate-800 border border-slate-200 outline-none focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all"
                  placeholder="Enter reason..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
              <button
                onClick={() => setShowEditLeaveModal(false)}
                disabled={isEditLeaveSubmitting}
                className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!leaveFormDate) { toast.error("Please select a date"); return; }
                  if (!leaveFormLeaveTypeId) { toast.error("Please select a leave type"); return; }
                  if (!leaveFormReason.trim()) { toast.error("Reason is required"); return; }

                  setIsEditLeaveSubmitting(true);
                  const loader = toast.loading("Saving changes...");
                  try {
                    const formData = new FormData();
                    formData.append("employeeId", id!);
                    formData.append("leaveTypeId", leaveFormLeaveTypeId);
                    formData.append("leaveMode", leaveFormType);
                    formData.append("startDate", leaveFormDate);
                    if (leaveFormType === "MULTIPLE" && leaveFormEndDate) {
                      formData.append("endDate", leaveFormEndDate);
                    }
                    formData.append("reason", leaveFormReason.trim());
                    if (leaveFormDocument) {
                      formData.append("documentUrls", leaveFormDocument);
                    }
                    const res = await updateHrLeaveRequest(selectedLeaveForEdit.id || selectedLeaveForEdit._id, formData);
                    if (res.success) {
                      toast.success("Leave request updated successfully!", { id: loader });
                      setShowEditLeaveModal(false);
                      loadLeaveData();
                    } else {
                      toast.error(res.message || "Failed to update leave request", { id: loader });
                    }
                  } catch (err: any) {
                    toast.error(err?.message || "Failed to update leave request", { id: loader });
                  } finally {
                    setIsEditLeaveSubmitting(false);
                  }
                }}
                disabled={isEditLeaveSubmitting}
                className="px-6 py-2 bg-[#212121] text-white rounded-xl text-xs font-bold hover:bg-[#333] transition cursor-pointer disabled:opacity-60 flex items-center gap-2"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE LEAVE CONFIRMATION MODAL ───────────── */}
      {showDeleteLeaveModal && selectedLeaveForDelete && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !isDeleteLeaveSubmitting && setShowDeleteLeaveModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">Delete Leave</h2>
              <button
                onClick={() => !isDeleteLeaveSubmitting && setShowDeleteLeaveModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 cursor-pointer bg-transparent border-none outline-none"
              >
                <IconX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-xs font-semibold text-slate-600">
                Are you sure you want to delete this leave?
              </p>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/20">
              <button
                onClick={() => setShowDeleteLeaveModal(false)}
                disabled={isDeleteLeaveSubmitting}
                className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setIsDeleteLeaveSubmitting(true);
                  const loader = toast.loading("Deleting leave request...");
                  try {
                    const res = await deleteHrLeaveRequest(selectedLeaveForDelete.id || selectedLeaveForDelete._id);
                    if (res.success) {
                      toast.success("Leave request deleted successfully!", { id: loader });
                      setShowDeleteLeaveModal(false);
                      loadLeaveData();
                    } else {
                      toast.error(res.message || "Failed to delete leave request", { id: loader });
                    }
                  } catch (err: any) {
                    toast.error(err.message || "Failed to delete leave request", { id: loader });
                  } finally {
                    setIsDeleteLeaveSubmitting(false);
                  }
                }}
                disabled={isDeleteLeaveSubmitting}
                className="px-6 py-2 bg-red-600 text-white rounded-xl text-xs font-bold hover:bg-red-700 transition cursor-pointer disabled:opacity-60"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT BANK DETAILS MODAL ───────────────────── */}
      {showEditBankModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !isBankSubmitting && setShowEditBankModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl z-10 my-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-[#212121]">Edit Bank Details</h2>
              <button onClick={() => setShowEditBankModal(false)} disabled={isBankSubmitting}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-550 cursor-pointer bg-transparent border-none outline-none">
                <IconX size={16} />
              </button>
            </div>
            <form onSubmit={handleEditBankSubmit}>
              <div className="p-6 grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto hide-scrollbar">
                
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Account Holder Name <span className="text-red-500">*</span></label>
                  <input type="text" required value={bankAccountHolderName} onChange={e => setBankAccountHolderName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Account Number <span className="text-red-500">*</span></label>
                  <input type="text" required value={bankAccountNumber} onChange={e => setBankAccountNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Account Type <span className="text-red-500">*</span></label>
                  <select value={bankAccountType} onChange={e => setBankAccountType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] bg-white">
                    <option value="Savings Account">Savings Account</option>
                    <option value="Current Account">Current Account</option>
                    <option value="Salary Account">Salary Account</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">IFSC Code <span className="text-red-500">*</span></label>
                  <input type="text" required value={bankIfscCode} onChange={e => setBankIfscCode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Bank Name <span className="text-red-500">*</span></label>
                  <input type="text" required value={bankName} onChange={e => setBankName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Branch Name <span className="text-red-500">*</span></label>
                  <input type="text" required value={bankBranchName} onChange={e => setBankBranchName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                </div>

                <div className="col-span-2 space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Branch Address <span className="text-red-500">*</span></label>
                  <textarea required value={bankBranchAddress} onChange={e => setBankBranchAddress(e.target.value)} rows={3}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] resize-none" />
                </div>

              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/20">
                <button type="button" onClick={() => setShowEditBankModal(false)} disabled={isBankSubmitting}
                  className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white">Cancel</button>
                <button type="submit" disabled={isBankSubmitting}
                  className="px-6 py-2 bg-[#212121] text-white rounded-xl text-xs font-bold hover:bg-[#333] transition cursor-pointer disabled:opacity-60">
                  {isBankSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── LENT/RETURN REQUESTS MODAL ────────────────── */}
      {showRequestsModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowRequestsModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl z-10 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">Lent/Return Requests</h2>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setShowSendRequestModal(true)}
                  className="px-4 py-1.5 bg-[#212121] hover:bg-black text-white text-[11px] font-bold rounded-lg transition cursor-pointer"
                >
                  Send Request
                </button>
                <button onClick={() => setShowRequestsModal(false)}
                  className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 cursor-pointer bg-transparent border-none outline-none">
                  <IconX size={16} />
                </button>
              </div>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto hide-scrollbar">
              {assetRequests.length === 0 ? (
                <div className="py-16 text-center flex flex-col justify-center items-center">
                  <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mb-3">
                    <IconFileText size={28} className="text-slate-300" />
                  </div>
                  <h3 className="text-xs font-bold text-slate-700">No assets found</h3>
                  <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                    We couldn't find any data to show here.
                  </p>
                </div>
              ) : (
                <div className="border border-slate-100 rounded-2xl overflow-hidden shadow-xs">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-gray-500 font-bold">
                        <th className="px-4 py-3">Asset</th>
                        <th className="px-4 py-3">Return Date</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Remark</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Approved By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 font-semibold text-slate-700">
                      {assetRequests.map((req: any, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/20 transition-colors">
                          <td className="px-4 py-3 text-[#212121]">{req.asset}</td>
                          <td className="px-4 py-3 text-gray-550">{req.returnDate || "-"}</td>
                          <td className="px-4 py-3">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${req.type === "LENT" ? "bg-blue-50 text-blue-600" : "bg-orange-50 text-orange-600"}`}>
                              {req.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 max-w-[120px] truncate">{req.remark || "-"}</td>
                          <td className="px-4 py-3">
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-600">
                              {req.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-550">{req.approvedBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── SEND LENT/RETURN REQUEST FORM MODAL ───────── */}
      {showSendRequestModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => !isReqSubmitting && setShowSendRequestModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-[#212121]">Send Asset Request</h2>
              <button onClick={() => setShowSendRequestModal(false)} disabled={isReqSubmitting}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-550 cursor-pointer bg-transparent border-none outline-none">
                <IconX size={16} />
              </button>
            </div>
            <form onSubmit={handleSendRequestSubmit}>
              <div className="p-6 space-y-4">
                
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Asset Name <span className="text-red-500">*</span></label>
                  <input type="text" required placeholder="e.g. MacBook Pro, Office Chair" value={reqAsset} onChange={e => setReqAsset(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Request Type <span className="text-red-500">*</span></label>
                  <div className="flex gap-4 text-xs font-semibold">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={reqType === "LENT"} onChange={() => setReqType("LENT")} className="accent-[#212121]" />
                      <span className="text-slate-700">Lent Request</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={reqType === "RETURN"} onChange={() => setReqType("RETURN")} className="accent-[#212121]" />
                      <span className="text-slate-700">Return Request</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Return Date (optional)</label>
                  <input type="date" value={reqReturnDate} onChange={e => setReqReturnDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Remark</label>
                  <textarea placeholder="Optional remark..." value={reqRemark} onChange={e => setReqRemark(e.target.value)} rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] resize-none" />
                </div>

              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
                <button type="button" onClick={() => setShowSendRequestModal(false)} disabled={isReqSubmitting}
                  className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white">Cancel</button>
                <button type="submit" disabled={isReqSubmitting}
                  className="px-6 py-2 bg-[#212121] text-white rounded-xl text-xs font-bold hover:bg-[#333] transition cursor-pointer disabled:opacity-60">
                  {isReqSubmitting ? "Sending..." : "Send Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD APPRECIATION MODAL ────────────────────── */}
      {showAddAppreciationModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowAddAppreciationModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-[#1E293B]">Add Appreciation</h2>
              <button onClick={() => setShowAddAppreciationModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-550 cursor-pointer bg-transparent border-none outline-none">
                <IconX size={16} />
              </button>
            </div>
            <form onSubmit={handleAddAppreciationSubmit}>
              <div className="p-6 space-y-4">
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Award Type <span className="text-red-500">*</span></label>
                    <div className="flex gap-2">
                      <select required value={appreciationAwardType} onChange={e => setAppreciationAwardType(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] bg-white">
                        <option value="">Select Award</option>
                        {appreciationAwardTypesList.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <button type="button" onClick={() => setShowNewAwardTypeModal(true)}
                        className="p-2 bg-black text-white rounded-xl hover:bg-slate-800 transition text-xs font-bold">
                        <IconPlus size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Employee <span className="text-red-500">*</span></label>
                    <div className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-xl bg-slate-50 text-xs font-semibold text-[#212121]">
                      <span>{emp.name || "Test"}</span>
                      <IconX size={14} className="text-slate-400 cursor-pointer hover:text-slate-600" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Recognized by <span className="text-red-500">*</span></label>
                    <select required value={appreciationRecognizedBy} onChange={e => setAppreciationRecognizedBy(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] bg-white">
                      <option value="">Select Recognizer</option>
                      <option value="Sanju">Sanju (Manager)</option>
                      <option value="Sushil Rathore">Sushil Rathore (HR)</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Recognition Date <span className="text-red-500">*</span></label>
                    <input type="date" required value={appreciationGivenOn} onChange={e => setAppreciationGivenOn(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Visibility <span className="text-red-500">*</span></label>
                  <select required value={appreciationVisibility} onChange={e => setAppreciationVisibility(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] bg-white">
                    <option value="Public">Public</option>
                    <option value="Private">Private</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Summary <span className="text-red-500">*</span></label>
                  <textarea required placeholder="Enter your summary" value={appreciationSummary} onChange={e => setAppreciationSummary(e.target.value)} rows={3}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] resize-none" />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Attachment</label>
                  <div className="border-2 border-dashed border-slate-200 rounded-2xl p-6 text-center cursor-pointer hover:bg-slate-50 transition-colors flex flex-col items-center justify-center space-y-2">
                    <div className="w-10 h-10 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-center">
                      <IconUpload size={18} className="text-slate-400" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-800">Drag & drop or click to browse</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">Maximum 10 files • Max 3MB per file</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">Accepted: image/*, application/pdf</p>
                    </div>
                  </div>
                </div>

              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
                <button type="button" onClick={() => setShowAddAppreciationModal(false)}
                  className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white">Cancel</button>
                <button type="submit"
                  className="px-6 py-2 bg-black text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition cursor-pointer">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD DYNAMIC AWARD TYPE MODAL ──────────────── */}
      {showNewAwardTypeModal && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowNewAwardTypeModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm z-10 animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-sm font-bold text-[#1E293B]">Add New Award Type</h3>
              <button onClick={() => setShowNewAwardTypeModal(false)} className="p-1 text-slate-400 hover:bg-slate-50 rounded-lg"><IconX size={14} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-[#1E293B]">Award Name</label>
                <input type="text" placeholder="e.g. Sales Rockstar" value={newAwardTypeNameInput} onChange={e => setNewAwardTypeNameInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/20">
              <button onClick={() => setShowNewAwardTypeModal(false)} className="px-4 py-1.5 border border-slate-200 text-xs font-semibold rounded-lg bg-white">Cancel</button>
              <button onClick={() => {
                if (newAwardTypeNameInput.trim()) {
                  setAppreciationAwardTypesList(prev => [...prev, newAwardTypeNameInput.trim()]);
                  setAppreciationAwardType(newAwardTypeNameInput.trim());
                  setNewAwardTypeNameInput("");
                  setShowNewAwardTypeModal(false);
                  toast.success("Award type added!");
                }
              }} className="px-4 py-1.5 bg-black text-white text-xs font-bold rounded-lg hover:bg-slate-800 transition">Add</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD APPRAISAL MODAL ───────────────────────── */}
      {showAddAppraisalModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowAddAppraisalModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-[#1E293B]">Add New Appraisal</h2>
              <button onClick={() => setShowAddAppraisalModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-555 cursor-pointer bg-transparent border-none outline-none">
                <IconX size={16} />
              </button>
            </div>
            <form onSubmit={handleAddAppraisalSubmit}>
              <div className="p-6 space-y-4">
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Employee <span className="text-red-500">*</span></label>
                    <div className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-xl bg-slate-50 text-xs font-semibold text-[#212121]">
                      <span>{emp.name || "Test"}</span>
                      <IconX size={14} className="text-slate-400 cursor-pointer hover:text-slate-600" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Old Designation</label>
                    <div className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-xl bg-slate-50 text-xs font-semibold text-[#212121]">
                      <span>{emp.designation || "Software Engineer"}</span>
                      <IconX size={14} className="text-slate-400 cursor-pointer hover:text-slate-600" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">New Designation</label>
                    <select value={appraisalNewDesignation} onChange={e => setAppraisalNewDesignation(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] bg-white">
                      <option value="">Select Designation...</option>
                      {designationsList.map((d: any) => (
                        <option key={d.id || d._id} value={d.designationName || d.name}>{d.designationName || d.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Effective Date <span className="text-red-500">*</span></label>
                    <input type="date" required value={appraisalEffectiveDate} onChange={e => setAppraisalEffectiveDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Old CTC</label>
                    <input type="number" placeholder="Enter old CTC" value={appraisalOldCtc} onChange={e => setAppraisalOldCtc(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">New CTC</label>
                    <input type="number" placeholder="Enter new CTC" value={appraisalNewCtc} onChange={e => setAppraisalNewCtc(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Increment Amount</label>
                    <div className="w-full px-3 py-2 border border-slate-100 rounded-xl bg-slate-50/50 text-xs font-bold text-slate-700">
                      {((parseFloat(appraisalNewCtc) || 0) - (parseFloat(appraisalOldCtc) || 0)) > 0 
                        ? `₹ ${((parseFloat(appraisalNewCtc) || 0) - (parseFloat(appraisalOldCtc) || 0)).toLocaleString()}`
                        : "Auto-calculated"}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Increment Percentage</label>
                    <div className="w-full px-3 py-2 border border-slate-100 rounded-xl bg-slate-50/50 text-xs font-bold text-slate-700">
                      {(parseFloat(appraisalOldCtc) || 0) > 0 && ((parseFloat(appraisalNewCtc) || 0) - (parseFloat(appraisalOldCtc) || 0)) > 0
                        ? `${((((parseFloat(appraisalNewCtc) || 0) - (parseFloat(appraisalOldCtc) || 0)) / (parseFloat(appraisalOldCtc) || 0)) * 100).toFixed(1)} %`
                        : "Auto-calculated"}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Remarks <span className="text-red-500">*</span></label>
                  <textarea required placeholder="Enter remarks..." value={appraisalRemarks} onChange={e => setAppraisalRemarks(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] resize-none" />
                </div>

              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
                <button type="button" onClick={() => setShowAddAppraisalModal(false)}
                  className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white">Cancel</button>
                <button type="submit"
                  className="px-6 py-2 bg-black text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition cursor-pointer">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD RELEGATION MODAL ──────────────────────── */}
      {showAddRelegationModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowAddRelegationModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-[#1E293B]">Add New Relegation</h2>
              <button onClick={() => setShowAddRelegationModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-555 cursor-pointer bg-transparent border-none outline-none">
                <IconX size={16} />
              </button>
            </div>
            <form onSubmit={handleAddRelegationSubmit}>
              <div className="p-6 space-y-4">
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Employee <span className="text-red-500">*</span></label>
                    <div className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-xl bg-slate-50 text-xs font-semibold text-[#212121]">
                      <span>{emp.name || "Test"}</span>
                      <IconX size={14} className="text-slate-400 cursor-pointer hover:text-slate-600" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Old Designation</label>
                    <div className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-xl bg-slate-50 text-xs font-semibold text-[#212121]">
                      <span>{emp.designation || "Software Engineer"}</span>
                      <IconX size={14} className="text-slate-400 cursor-pointer hover:text-slate-600" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">New Designation</label>
                    <select value={relegationNewDesignation} onChange={e => setRelegationNewDesignation(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] bg-white">
                      <option value="">Select Designation...</option>
                      {designationsList.map((d: any) => (
                        <option key={d.id || d._id} value={d.designationName || d.name}>{d.designationName || d.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Effective Date <span className="text-red-500">*</span></label>
                    <input type="date" required value={relegationEffectiveDate} onChange={e => setRelegationEffectiveDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Old CTC</label>
                    <input type="number" placeholder="Enter old CTC" value={relegationOldCtc} onChange={e => setRelegationOldCtc(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">New CTC</label>
                    <input type="number" placeholder="Enter new CTC" value={relegationNewCtc} onChange={e => setRelegationNewCtc(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Decrement Amount</label>
                    <div className="w-full px-3 py-2 border border-slate-100 rounded-xl bg-slate-50/50 text-xs font-bold text-slate-700">
                      {((parseFloat(relegationOldCtc) || 0) - (parseFloat(relegationNewCtc) || 0)) > 0 
                        ? `₹ ${((parseFloat(relegationOldCtc) || 0) - (parseFloat(relegationNewCtc) || 0)).toLocaleString()}`
                        : "Auto-calculated"}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Decrement Percentage</label>
                    <div className="w-full px-3 py-2 border border-slate-100 rounded-xl bg-slate-50/50 text-xs font-bold text-slate-700">
                      {(parseFloat(relegationOldCtc) || 0) > 0 && ((parseFloat(relegationOldCtc) || 0) - (parseFloat(relegationNewCtc) || 0)) > 0
                        ? `${((((parseFloat(relegationOldCtc) || 0) - (parseFloat(relegationNewCtc) || 0)) / (parseFloat(relegationOldCtc) || 0)) * 100).toFixed(1)} %`
                        : "Auto-calculated"}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Remarks <span className="text-red-500">*</span></label>
                  <textarea required placeholder="Enter remarks..." value={relegationRemarks} onChange={e => setRelegationRemarks(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] resize-none" />
                </div>

              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
                <button type="button" onClick={() => setShowAddRelegationModal(false)}
                  className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white">Cancel</button>
                <button type="submit"
                  className="px-6 py-2 bg-black text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition cursor-pointer">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD FINE/WARNING MODAL ────────────────────── */}
      {showAddFineWarningModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowAddFineWarningModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-[#1E293B]">Add Fine / Warning</h2>
              <button onClick={() => setShowAddFineWarningModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-550 cursor-pointer bg-transparent border-none outline-none">
                <IconX size={16} />
              </button>
            </div>
            <form onSubmit={handleAddFineWarningSubmit}>
              <div className="p-6 space-y-4">
                
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Employee <span className="text-red-500">*</span></label>
                  <div className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-xl bg-slate-50 text-xs font-semibold text-[#212121]">
                    <span>{emp.name || "Test"}</span>
                    <IconX size={14} className="text-slate-400 cursor-pointer hover:text-slate-600" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Type <span className="text-red-500">*</span></label>
                  <div className="flex gap-4 text-xs font-semibold">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={fineType === "FINE"} onChange={() => setFineType("FINE")} className="accent-[#212121]" />
                      <span className="text-slate-700">Fine</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={fineType === "WARNING"} onChange={() => setFineType("WARNING")} className="accent-[#212121]" />
                      <span className="text-slate-700">Warning</span>
                    </label>
                  </div>
                </div>

                {fineType === "FINE" && (
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Amount (₹) <span className="text-red-500">*</span></label>
                    <input type="number" required placeholder="e.g. 500" value={fineAmount} onChange={e => setFineAmount(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                  </div>
                )}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Date <span className="text-red-500">*</span></label>
                  <input type="date" required value={fineDate} onChange={e => setFineDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Reason <span className="text-red-500">*</span></label>
                  <textarea required placeholder="Reason for action..." value={fineReason} onChange={e => setFineReason(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] resize-none" />
                </div>
              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
                <button type="button" onClick={() => setShowAddFineWarningModal(false)}
                  className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white">Cancel</button>
                <button type="submit"
                  className="px-6 py-2 bg-black text-white rounded-xl text-xs font-bold hover:bg-[#333] transition cursor-pointer">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ADD REWARD MODAL ──────────────────────────── */}
      {showAddRewardModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowAddRewardModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="space-y-0.5">
                <h2 className="text-base font-bold text-[#1E293B]">Add Reward</h2>
                <p className="text-[10px] font-semibold text-slate-400">Enter reward details</p>
              </div>
              <button onClick={() => setShowAddRewardModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-550 cursor-pointer bg-transparent border-none outline-none">
                <IconX size={16} />
              </button>
            </div>
            <form onSubmit={handleAddRewardSubmit}>
              <div className="p-6 space-y-4">
                
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Reward Date <span className="text-red-500">*</span></label>
                  <input type="date" required value={rewardGivenOn} onChange={e => setRewardGivenOn(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                </div>

                <div className="flex items-center gap-2">
                  <input type="checkbox" id="monetaryCheckbox" checked={rewardIsMonetary} onChange={e => setRewardIsMonetary(e.target.checked)}
                    className="accent-black w-4 h-4 cursor-pointer" />
                  <label htmlFor="monetaryCheckbox" className="text-xs font-bold text-slate-700 cursor-pointer">Monetary</label>
                </div>

                {rewardIsMonetary && (
                  <div className="space-y-1.5 animate-in slide-in-from-top-2 duration-150">
                    <label className="block text-xs font-bold text-[#1E293B]">Reward Amount (₹) <span className="text-red-500">*</span></label>
                    <input type="number" required placeholder="Enter reward amount" value={rewardAmount} onChange={e => setRewardAmount(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Reward Description <span className="text-red-500">*</span></label>
                  <textarea required placeholder="Enter reward description" value={rewardDescription} onChange={e => setRewardDescription(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] resize-none" />
                </div>

              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
                <button type="button" onClick={() => setShowAddRewardModal(false)}
                  className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white">Cancel</button>
                <button type="submit"
                  className="px-6 py-2 bg-black text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition cursor-pointer">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}



      {/* ── RESIGN / TERMINATE MODAL ──────────────────── */}
      {showResignTerminateModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowResignTerminateModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-[#212121]">
                {resignTerminateActionType === "RESIGN" ? "Initiate Resignation" : "Terminate Employee"}
              </h2>
              <button onClick={() => setShowResignTerminateModal(false)}
                className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-555 cursor-pointer bg-transparent border-none outline-none">
                <IconX size={16} />
              </button>
            </div>
            <form onSubmit={handleResignTerminateSubmit}>
              <div className="p-6 space-y-4">
                
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Date of Action <span className="text-red-500">*</span></label>
                  <input type="date" required value={resignTerminateDate} onChange={e => setResignTerminateDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                </div>

                {resignTerminateActionType === "RESIGN" && (
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-[#1E293B]">Final Working Day <span className="text-red-500">*</span></label>
                    <input type="date" required value={resignTerminateFinalDay} onChange={e => setResignTerminateFinalDay(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121]" />
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-[#1E293B]">Reason / Remark <span className="text-red-500">*</span></label>
                  <textarea required placeholder="Detailed description..." value={resignTerminateReason} onChange={e => setResignTerminateReason(e.target.value)} rows={4}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs font-semibold text-[#212121] resize-none" />
                </div>

              </div>
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/20">
                <button type="button" onClick={() => setShowResignTerminateModal(false)}
                  className="px-5 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer bg-white">Cancel</button>
                <button type="submit"
                  className={`px-6 py-2 text-white rounded-xl text-xs font-bold transition cursor-pointer ${
                    resignTerminateActionType === "RESIGN" ? "bg-[#212121] hover:bg-[#333]" : "bg-red-650 hover:bg-red-700"
                  }`}>
                  Submit Action
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
